import React, { useState, useCallback, useRef, useEffect } from 'react';
import contentService from '../services/contentService';
import { flushSync } from 'react-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import Loading from '../components/Loading';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, ComposedChart
} from 'recharts';

/* ─── constants ──────────────────────────────────────────────────────── */
const FB_BLUE   = '#1877F2';
const IG_GRAD1  = '#833AB4';
const IG_GRAD2  = '#E1306C';
const IG_ORANGE = '#F77737';
const TK_RED    = '#EE1D52';
const TK_TEAL   = '#69C9D0';
const COLORS    = ['#1877F2', '#833AB4', '#E1306C', '#F77737', '#0D47A1', '#42B3F5'];

/* ─── helpers ────────────────────────────────────────────────────────── */
function fmt(n) {
  if (n === null || n === undefined || n === 'N/A') return '—';
  if (typeof n === 'string') return n;
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (Math.abs(n) >= 1_000)     return (n / 1_000).toFixed(1) + 'K';
  return n.toLocaleString('es-CL');
}
function shortDay(label) {
  if (!label) return '';
  const p = label.split(' ');
  return p.length >= 2 ? `${p[0]} ${p[1].slice(0, 3)}` : label;
}
function pct(v) {
  if (typeof v === 'string') return v;
  if (v === null || v === undefined) return '—';
  return v + '%';
}
function slugify(name) {
  return (name || 'unknown')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '') || 'marca';
}
/* Strips " Instagram Report", " Facebook", " TikTok …" suffixes from profile names */
function cleanBrandName(name) {
  if (!name) return 'Competencia';
  return name.replace(/\s+(instagram|facebook|tiktok)\b.*/i, '').trim() || name;
}

/* ─── parsers ────────────────────────────────────────────────────────── */

function detectPlatform(raw) {
  const r2 = raw[1] && raw[1][0] ? String(raw[1][0]) : '';
  if (r2.toLowerCase().includes('instagram')) return 'instagram';
  if (r2.toLowerCase().includes('facebook'))  return 'facebook';
  if (r2.toLowerCase().includes('tiktok'))    return 'tiktok';
  return 'unknown';
}

function buildTimeSeries(raw, i) {
  const days   = raw[i].slice(1).filter(Boolean);
  const values = raw[i + 1] ? raw[i + 1].slice(1) : [];
  return days.map((d, idx) => ({ day: d, value: (values[idx] === 'N/A' ? 0 : values[idx]) ?? 0 }));
}

/* Facebook parser */
function parseFacebook(raw) {
  const r = {
    platform: 'facebook', profile: null, dateRange: null,
    kpis: [], reachDist: [], viewsDist: [], followersDist: [],
    postsDist: [], engagementDist: [], topPosts: [],
    postTypes: [], postTypesByEngagement: [], postTypesByAvgEng: [],
    postTypesByAvgReach: [], countries: [], cities: [], kpiEvolution: [],
  };
  if (raw[1]) { r.profile = raw[1][0]; r.dateRange = raw[1][9]; }

  let i = 0;
  while (i < raw.length) {
    const c0 = raw[i] && raw[i][0];

    if (c0 === 'KPI') {
      i++;
      while (i < raw.length && raw[i] && raw[i][0]) {
        const [kpi, curr, prev, change] = raw[i];
        if (kpi) r.kpis.push({ kpi, curr, prev, pct: change });
        i++;
      }
      continue;
    }
    if (c0 === 'Day') {
      const series = buildTimeSeries(raw, i);
      const label  = raw[i + 1] && raw[i + 1][0];
      if (label === 'Reach')      r.reachDist = series;
      else if (label === 'Views') r.viewsDist = series;
      else if (label === 'Followers' && r.followersDist.length === 0) r.followersDist = series;
      else if (label === 'Posts')      r.postsDist = series;
      else if (label === 'Engagement' && r.engagementDist.length === 0) r.engagementDist = series;
      i += 2;
      continue;
    }
    if (c0 === 'Page' && raw[i][1] === 'Link') {
      i++;
      while (i < raw.length && raw[i] && raw[i][0]) {
        const row = raw[i];
        r.topPosts.push({ link: row[1], date: row[2], type: row[3], message: row[4],
          reach: row[8], reactions: row[9], comments: row[10], shares: row[11],
          engagement: row[12], engRate: row[13] });
        i++;
      }
      continue;
    }
    if (c0 === 'Post type' && raw[i][1] === 'Count')               { i++; while (i < raw.length && raw[i] && raw[i][0]) { r.postTypes.push({ type: raw[i][0], count: raw[i][1], pct: raw[i][2] }); i++; } continue; }
    if (c0 === 'Post type' && raw[i][1] === 'Engagement')           { i++; while (i < raw.length && raw[i] && raw[i][0]) { r.postTypesByEngagement.push({ type: raw[i][0], value: raw[i][1] }); i++; } continue; }
    if (c0 === 'Post type' && raw[i][1] === 'Average Engagement')   { i++; while (i < raw.length && raw[i] && raw[i][0]) { r.postTypesByAvgEng.push({ type: raw[i][0], value: parseFloat(raw[i][1]) }); i++; } continue; }
    if (c0 === 'Post type' && raw[i][1] === 'Average Reach')        { i++; while (i < raw.length && raw[i] && raw[i][0]) { r.postTypesByAvgReach.push({ type: raw[i][0], value: parseFloat(raw[i][1]) }); i++; } continue; }
    if (c0 === 'Country' && raw[i][1] === 'Count')                   { i++; while (i < raw.length && raw[i] && raw[i][0] && typeof raw[i][1] === 'number') { r.countries.push({ name: raw[i][0], count: raw[i][1], pct: raw[i][2] }); i++; } continue; }
    if (c0 === 'Profile' && raw[i][1] === 'Date' && raw[i][2] === 'Posts') {
      i++;
      while (i < raw.length && raw[i] && raw[i][1]) {
        r.kpiEvolution.push({ date: raw[i][1], posts: raw[i][2], engagement: raw[i][3],
          reactions: raw[i][4], comments: raw[i][5], shares: raw[i][6], clicks: raw[i][7] });
        i++;
      }
      continue;
    }
    // Cities (Santiago de Chile first city)
    if (c0 === 'Santiago de Chile, Chile') {
      i--;
      while (i < raw.length && raw[i] && raw[i][0] && raw[i][0] !== 'KPI Evolution Table') {
        if (raw[i][0] && typeof raw[i][1] === 'number') r.cities.push({ name: raw[i][0], count: raw[i][1], pct: raw[i][2] });
        i++;
      }
      continue;
    }
    i++;
  }
  return r;
}

/* Instagram parser */
function parseInstagram(raw) {
  const r = {
    platform: 'instagram', profile: null, dateRange: null,
    kpis: [], followerCountDist: [], followerGrowthDist: [],
    profileVisitsDist: [], postsDist: [], engagementDist: [],
    likesDist: [], commentsDist: [], topPosts: [], topStories: [],
    postTypes: [], postTypesByEngagement: [], postTypesByAvgEng: [],
    hashtags: [], genderAge: [], genders: [], countries: [], cities: [],
    kpiEvolution: [], engHeatmap: [],
  };
  if (raw[1]) { r.profile = raw[1][0]; r.dateRange = raw[1][9]; }

  let i = 0;
  while (i < raw.length) {
    const c0 = raw[i] && raw[i][0];
    const c1 = raw[i] && raw[i][1];

    if (c0 === 'KPI') {
      i++;
      while (i < raw.length && raw[i] && raw[i][0]) {
        const [kpi, curr, prev, change] = raw[i];
        if (kpi) r.kpis.push({ kpi, curr, prev, pct: change });
        i++;
      }
      continue;
    }
    if (c0 === 'Day') {
      const series = buildTimeSeries(raw, i);
      const label  = raw[i + 1] && raw[i + 1][0];
      if      (label === 'Follower count')   r.followerCountDist = series;
      else if (label === 'Follower growth')  r.followerGrowthDist = series;
      else if (label === 'Profile Visits')   r.profileVisitsDist = series;
      else if (label === 'Posts')            r.postsDist = series;
      else if (label === 'Engagement')       r.engagementDist = series;
      else if (label === 'Likes')            r.likesDist = series;
      else if (label === 'Comments')         r.commentsDist = series;
      i += 2;
      continue;
    }
    // Top posts header — dynamic column detection to support Message column
    if (c0 === 'Profile' && c1 === 'Link') {
      const hdr = raw[i];
      const col = {};
      hdr.forEach((v, idx) => { if (v) col[String(v).trim().toLowerCase()] = idx; });
      const iLink     = col['link']      ?? 1;
      const iDate     = col['date']      ?? 2;
      const iType     = col['type']      ?? 3;
      const iMsg      = col['message']   ?? col['caption'] ?? col['text'] ?? null;
      const iReach    = col['reach']     ?? 8;
      const iLikes    = col['likes']     ?? 9;
      const iComments = col['comments']  ?? 10;
      const iEng      = col['engagement']?? 11;
      const iEngRate  = col['engagement rate / post'] ?? col['engagement rate'] ?? col['eng. rate'] ?? 12;
      const iViews    = col['total views'] ?? col['views'] ?? 13;
      i++;
      while (i < raw.length && raw[i] && raw[i][0]) {
        const row = raw[i];
        r.topPosts.push({
          link:       row[iLink],
          date:       row[iDate],
          type:       row[iType],
          caption:    iMsg !== null ? row[iMsg] : null,
          message:    iMsg !== null ? row[iMsg] : null,
          reach:      row[iReach],
          likes:      row[iLikes],
          comments:   row[iComments],
          engagement: row[iEng],
          engRate:    row[iEngRate],
          views:      row[iViews],
        });
        i++;
      }
      continue;
    }
    // Top stories header
    if (c0 === 'Profile' && c1 === 'Date' && raw[i][2] === 'Type' && raw[i][3] === 'Reach') {
      i++;
      while (i < raw.length && raw[i] && raw[i][0]) {
        const row = raw[i];
        r.topStories.push({ date: row[1], type: row[2], reach: row[3], views: row[4],
          replies: row[5], tapsFwd: row[6], tapsBwd: row[7], exits: row[8] });
        i++;
      }
      continue;
    }
    // Post Types (count)
    if (c0 === 'Type' && c1 === 'Counts') {
      i++;
      while (i < raw.length && raw[i] && raw[i][0]) { r.postTypes.push({ type: raw[i][0], count: raw[i][1] }); i++; }
      continue;
    }
    if (c0 === 'Type' && c1 === 'Engagement') {
      i++;
      while (i < raw.length && raw[i] && raw[i][0]) { r.postTypesByEngagement.push({ type: raw[i][0], value: raw[i][1] }); i++; }
      continue;
    }
    if (c0 === 'Type' && c1 === 'Avg. engagement') {
      i++;
      while (i < raw.length && raw[i] && raw[i][0]) { r.postTypesByAvgEng.push({ type: raw[i][0], value: parseFloat(raw[i][1]) }); i++; }
      continue;
    }
    // Hashtags
    if (c0 === 'Hashtag' && c1 === 'Average Engagement') {
      i++;
      while (i < raw.length && raw[i] && raw[i][0] && raw[i][1] !== 'N/A') {
        r.hashtags.push({ tag: raw[i][0], value: raw[i][1] });
        i++;
      }
      continue;
    }
    // Gender + age
    if (c0 === '' && c1 === 'Male') {
      i++;
      while (i < raw.length && raw[i] && raw[i][0]) {
        const row = raw[i];
        r.genderAge.push({ age: row[0], male: row[1], female: row[2], malePct: row[3], femalePct: row[4] });
        i++;
      }
      continue;
    }
    // Gender summary
    if (c0 === 'Gender' && c1 === 'Count') {
      i++;
      while (i < raw.length && raw[i] && raw[i][0]) { r.genders.push({ gender: raw[i][0], count: raw[i][1], pct: raw[i][2] }); i++; }
      continue;
    }
    // Countries
    if (c0 === 'Country' && c1 === 'Count') {
      i++;
      while (i < raw.length && raw[i] && raw[i][0] && typeof raw[i][1] === 'number') {
        r.countries.push({ name: raw[i][0], count: raw[i][1], pct: raw[i][2] });
        i++;
      }
      continue;
    }
    // Cities
    if (c0 === 'City' && c1 === 'Count') {
      i++;
      while (i < raw.length && raw[i] && raw[i][0] && typeof raw[i][1] === 'number') {
        r.cities.push({ name: raw[i][0], count: raw[i][1], pct: raw[i][2] });
        i++;
      }
      continue;
    }
    // KPI Evolution
    if (c0 === 'Profile' && c1 === 'Date' && raw[i][2] === 'Posts') {
      i++;
      while (i < raw.length && raw[i] && raw[i][1]) {
        r.kpiEvolution.push({
          date: raw[i][1], posts: raw[i][2], engagement: raw[i][3],
          likes: raw[i][4], comments: raw[i][5], saves: raw[i][6],
          videoViews: raw[i][7], reach: raw[i][8], impressions: raw[i][9],
        });
        i++;
      }
      continue;
    }
    // Engagement heatmap
    if (c0 === '' && raw[i][1] === '0') {
      const hours = raw[i].slice(1).filter(v => v !== null);
      i++;
      const rows = [];
      while (i < raw.length && raw[i] && raw[i][0] && ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].includes(raw[i][0])) {
        const values = raw[i].slice(1).map(v => (v === null || v === 'N/A') ? 0 : parseInt(v, 10) || 0);
        rows.push({ day: raw[i][0], values });
        i++;
      }
      r.engHeatmap = { hours: hours.map(String), rows };
      continue;
    }
    i++;
  }
  return r;
}

/* TikTok parser */
function parseTikTok(raw) {
  const r = {
    platform: 'tiktok', profile: null, dateRange: null,
    kpis: [], followerCountDist: [], followerGrowthDist: [],
    postsDist: [], sharesDist: [], viewsDist: [], savesDist: [],
    engagementDist: [], likesDist: [], commentsDist: [],
    topPosts: [], hashtagsByUsage: [], hashtagsByEng: [],
    engHeatmap: [],
  };
  if (raw[1]) { r.profile = raw[1][0]; r.dateRange = raw[1][9]; }

  let i = 0;
  while (i < raw.length) {
    const c0 = raw[i] && raw[i][0];
    const c1 = raw[i] && raw[i][1];

    if (c0 === 'KPI') {
      i++;
      while (i < raw.length && raw[i] && raw[i][0]) {
        const [kpi, curr, prev, change] = raw[i];
        if (kpi) r.kpis.push({ kpi, curr, prev, pct: change });
        i++;
      }
      continue;
    }
    if (c0 === 'Day') {
      const series = buildTimeSeries(raw, i);
      const label  = raw[i + 1] && raw[i + 1][0];
      if      (label === 'Follower count')  r.followerCountDist = series;
      else if (label === 'Followers diff')  r.followerGrowthDist = series;
      else if (label === 'Posts')           r.postsDist = series;
      else if (label === 'Shares')          r.sharesDist = series;
      else if (label === 'Views')           r.viewsDist = series;
      else if (label === 'Saves')           r.savesDist = series;
      else if (label === 'Engagement')      r.engagementDist = series;
      else if (label === 'Likes')           r.likesDist = series;
      else if (label === 'Comments')        r.commentsDist = series;
      i += 2;
      continue;
    }
    // Top Posts — detect columns dynamically from header row
    if (c0 === 'Profile' && c1 === 'Link') {
      const hdr = raw[i];
      const col = {};
      hdr.forEach((v, idx) => { if (v) col[String(v).trim().toLowerCase()] = idx; });
      // Map flexible column names
      const iLink    = col['link']    ?? 1;
      const iDate    = col['date']    ?? 2;
      const iType    = col['type']    ?? 3;
      const iMsg     = col['message'] ?? col['caption'] ?? col['text'] ?? null;
      const iReach   = col['reach']   ?? 8;
      const iLikes   = col['likes']   ?? 9;
      const iComments= col['comments']?? 10;
      const iEng     = col['engagement'] ?? 11;
      const iEngRate = col['engagement rate / post'] ?? col['engagement rate'] ?? col['eng. rate'] ?? 12;
      const iViews   = col['total views'] ?? col['views'] ?? 13;
      i++;
      while (i < raw.length && raw[i] && raw[i][0]) {
        const row = raw[i];
        r.topPosts.push({
          link:       row[iLink],
          date:       row[iDate],
          type:       row[iType],
          caption:    iMsg !== null ? row[iMsg] : null,
          reach:      row[iReach],
          likes:      row[iLikes],
          comments:   row[iComments],
          engagement: row[iEng],
          engRate:    row[iEngRate],
          views:      row[iViews],
        });
        i++;
      }
      continue;
    }
    // Hashtags by usage
    if (c0 === 'Hashtag' && c1 === 'Counts') {
      i++;
      while (i < raw.length && raw[i] && raw[i][0]) {
        r.hashtagsByUsage.push({ tag: raw[i][0], count: raw[i][1] });
        i++;
      }
      continue;
    }
    // Hashtags by avg engagement
    if (c0 === 'Hashtag' && c1 === 'Average Engagement') {
      i++;
      while (i < raw.length && raw[i] && raw[i][0]) {
        r.hashtagsByEng.push({ tag: raw[i][0], value: raw[i][1] });
        i++;
      }
      continue;
    }
    // Heatmap
    if (c0 === '' && c1 === '0') {
      const hours = raw[i].slice(1).filter(v => v !== null);
      i++;
      const rows = [];
      while (i < raw.length && raw[i] && raw[i][0] && ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].includes(raw[i][0])) {
        const values = raw[i].slice(1).map(v => (v === null || v === 'N/A') ? 0 : parseInt(v, 10) || 0);
        rows.push({ day: raw[i][0], values });
        i++;
      }
      r.engHeatmap = { hours: hours.map(String), rows };
      continue;
    }
    i++;
  }
  return r;
}

function parseReport(wb) {
  const ws  = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
  const platform = detectPlatform(raw);
  if (platform === 'instagram') return parseInstagram(raw);
  if (platform === 'tiktok')    return parseTikTok(raw);
  return parseFacebook(raw);
}

/* Competencia parser — bypasses detectPlatform, uses explicit platform */
function parseCompReport(wb, platform) {
  const ws  = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
  if (platform === 'instagram') return { ...parseInstagram(raw), platform: 'instagram', isComp: true };
  if (platform === 'tiktok')    return { ...parseTikTok(raw),    platform: 'tiktok',    isComp: true };
  return { ...parseFacebook(raw), platform: 'facebook', isComp: true };
}

/* ─── responsive CSS injection ───────────────────────────────────────── */
const RESPONSIVE_CSS = `
@media (max-width: 680px) {
  .rp-page    { padding: 12px 12px !important; }
  .rp-header  { flex-direction: column !important; gap: 8px !important; align-items: flex-start !important; }
  .rp-header-actions { width: 100% !important; justify-content: flex-end !important; }
  .rp-tabs    { overflow-x: auto !important; -webkit-overflow-scrolling: touch !important;
                flex-wrap: nowrap !important; padding-bottom: 4px !important;
                scrollbar-width: none !important; }
  .rp-tabs::-webkit-scrollbar { display: none !important; }
  .rp-tab-add { display: none !important; }
  .rp-content { gap: 10px !important; }
  .rp-row     { flex-direction: column !important; gap: 10px !important; }
  .rp-card-fixed { flex: 1 1 100% !important; min-width: 0 !important; }
  .rp-kpi-grid   { grid-template-columns: repeat(2,1fr) !important; gap: 8px !important; }
  .rp-totales    { grid-template-columns: repeat(2,1fr) !important; gap: 8px !important; }
  .rp-plat-grid  { grid-template-columns: 1fr !important; }
  .rp-tab-btn    { padding: 8px 10px !important; font-size: 13px !important; white-space: nowrap !important; }
  .rp-page-title { font-size: 17px !important; }
  .rp-subtitle   { font-size: 11px !important; }
}
`;

/* ─── pie label renderer ─────────────────────────────────────────────── */
function makePieLabel(textFn) {
  const RADIAN = Math.PI / 180;
  return (props) => {
    const { cx, cy, midAngle, outerRadius, percent } = props;
    if (percent < 0.05) return null;                          // oculta slices muy pequeños
    const r = outerRadius + 14;
    const x = cx + r * Math.cos(-midAngle * RADIAN);
    const y = cy + r * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x} y={y}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fill="#555"
        fontSize={10}
        fontWeight={500}
        fontFamily='-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      >
        {textFn(props)}
      </text>
    );
  };
}

/* ─── shared UI components ───────────────────────────────────────────── */

function SectionTitle({ children }) {
  return <h3 style={S.sectionTitle}>{children}</h3>;
}

function Card({ children, flex, className }) {
  return <div data-pdf-block="true" className={className} style={{ ...S.card, ...(flex ? { flex } : {}) }}>{children}</div>;
}

function KpiCard({ kpi, curr, prev, pct: change, accent }) {
  const up   = typeof change === 'number' && change > 0;
  const down = typeof change === 'number' && change < 0;
  return (
    <div style={{ ...S.kpiCard, borderTop: `3px solid ${accent}` }}>
      <div style={S.kpiLabel}>{kpi}</div>
      <div style={{ ...S.kpiValue, color: accent }}>{fmt(curr)}</div>
      {change !== 0 && change !== null && typeof change === 'number' && (
        <div style={{ ...S.kpiChange, color: up ? '#2e7d32' : down ? '#c62828' : '#666' }}>
          {up ? '▲' : '▼'} {Math.abs(change)}%
          <span style={S.kpiPrev}> vs {fmt(prev)}</span>
        </div>
      )}
    </div>
  );
}

function TimeChart({ data, label, color, height = 190, autoScale = false }) {
  if (!data || data.length === 0) return null;
  const vals = data.map(d => d.value).filter(v => typeof v === 'number');
  const minVal = Math.min(...vals);
  const maxVal = Math.max(...vals);
  const pad = (maxVal - minVal) * 0.08 || 1;
  const domain = autoScale
    ? [Math.max(0, Math.floor(minVal - pad)), Math.ceil(maxVal + pad)]
    : [0, 'auto'];
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={`g${label}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor={color} stopOpacity={0.25} />
            <stop offset="95%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="day" tickFormatter={shortDay} tick={{ fontSize: 10 }}
          interval={Math.max(0, Math.floor(data.length / 7) - 1)} />
        <YAxis tick={{ fontSize: 10 }} width={52} tickFormatter={fmt} domain={domain} />
        <Tooltip labelFormatter={shortDay} formatter={v => [fmt(v), label]} />
        <Area type="monotone" dataKey="value" stroke={color}
          fill={`url(#g${label})`} strokeWidth={2} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function HBarChart({ data, dataKey = 'value', nameKey = 'type', color, label, height = 180 }) {
  if (!data || data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 20, left: 60, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={fmt} />
        <YAxis type="category" dataKey={nameKey} tick={{ fontSize: 12 }} width={60} />
        <Tooltip formatter={v => [fmt(v), label || dataKey]} />
        <Bar dataKey={dataKey} fill={color} radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function PostCard({ post, idx, accent, platform }) {
  const [exp, setExp] = useState(false);
  const caption = post.message || post.caption || '';

  return (
    <div style={S.postCard}>
      <div style={{ ...S.postRank, color: accent }}>#{idx + 1}</div>
      <div style={{ flex: 1 }}>
        <div style={S.postMeta}>
          <span style={{ ...S.postType, background: accent }}>{post.type}</span>
          <span style={S.postDate}>{post.date}</span>
          {post.link && (
            <a href={post.link} target="_blank" rel="noopener noreferrer" style={{ ...S.postLink, color: accent }}>
              Ver en {platform === 'instagram' ? 'Instagram' : 'Facebook'} ↗
            </a>
          )}
        </div>

        {/* Caption — viene del Excel */}
        {caption && (
          <div style={S.postMessage} onClick={() => setExp(!exp)}>
            {exp ? caption : caption.slice(0, 140) + (caption.length > 140 ? '… ' : '')}
            {!exp && caption.length > 140 && (
              <span style={{ color: accent, fontWeight: 600, fontSize: 12, marginLeft: 4 }}>ver más</span>
            )}
          </div>
        )}

        <div style={S.postStats}>
          {platform === 'instagram' ? <>
            <Stat label="Likes"       value={fmt(post.likes)} />
            <Stat label="Comments"    value={fmt(post.comments)} />
            <Stat label="Interacciones"  value={fmt(post.engagement)} bold />
            <Stat label="Eng. Rate"      value={post.engRate ? String(post.engRate).slice(0, 6) + '%' : '—'} />
          </> : <>
            <Stat label="Reach"          value={fmt(post.reach)} />
            <Stat label="Reactions"      value={fmt(post.reactions)} />
            <Stat label="Comments"       value={fmt(post.comments)} />
            <Stat label="Shares"         value={fmt(post.shares)} />
            <Stat label="Interacciones"  value={fmt(post.engagement)} bold />
          </>}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, bold }) {
  return (
    <div style={S.stat}>
      <div style={{ ...S.statValue, fontWeight: bold ? 700 : 500 }}>{value}</div>
      <div style={S.statLabel}>{label}</div>
    </div>
  );
}

/* ─── Competencia Instagram PostCard (no caption loading) ───────────── */
function CompetenciaPostCard({ post, idx, accent }) {
  const [exp, setExp] = useState(false);
  const msg = post.message || post.caption || '';
  return (
    <div style={S.postCard}>
      <div style={{ ...S.postRank, color: accent }}>#{idx + 1}</div>
      <div style={{ flex: 1 }}>
        <div style={S.postMeta}>
          <span style={{ ...S.postType, background: accent }}>{post.type}</span>
          <span style={S.postDate}>{post.date}</span>
          {post.link && (
            <a href={post.link} target="_blank" rel="noopener noreferrer" style={{ ...S.postLink, color: accent }}>
              Ver publicación ↗
            </a>
          )}
        </div>
        {msg && (
          <div style={S.postMessage} onClick={() => setExp(!exp)}>
            {exp ? msg : msg.slice(0, 140) + (msg.length > 140 ? '… ' : '')}
            {!exp && msg.length > 140 && <span style={{ color: accent, fontWeight: 600, fontSize: 12 }}> ver más</span>}
          </div>
        )}
        <div style={S.postStats}>
          <Stat label="Reach"      value={fmt(post.reach)} />
          <Stat label="Likes"      value={fmt(post.likes)} />
          <Stat label="Comments"   value={fmt(post.comments)} />
          <Stat label="Interacciones" value={fmt(post.engagement)} bold />
        </div>
      </div>
    </div>
  );
}

/* ─── Engagement Heatmap ─────────────────────────────────────────────── */
function EngHeatmap({ data, color = '131,58,180' }) {
  if (!data || !data.rows || data.rows.length === 0) return null;
  const allVals = data.rows.flatMap(r => r.values);
  const maxVal  = Math.max(...allVals, 1);
  const days    = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '36px ' + data.hours.map(() => '1fr').join(' '), gap: 2, minWidth: 600 }}>
        <div />
        {data.hours.map(h => <div key={h} style={{ fontSize: 9, textAlign: 'center', color: '#888' }}>{h}</div>)}
        {data.rows.map((row, ri) => (
          <React.Fragment key={row.day}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'flex', alignItems: 'center' }}>{days[ri] || row.day}</div>
            {row.values.map((v, ci) => {
              const intensity = v / maxVal;
              return (
                <div key={ci} title={`${days[ri]} ${data.hours[ci]}h: ${v}`}
                  style={{ height: 20, borderRadius: 3, background: v > 0 ? `rgba(${color},${0.1 + intensity * 0.9})` : '#f5f5f5' }} />
              );
            })}
          </React.Fragment>
        ))}
      </div>
      <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>Hora del día (0 = medianoche)</div>
    </div>
  );
}

/* ─── Facebook dashboard ─────────────────────────────────────────────── */
const FB_HERO = ['Total Posts', 'Total Engagement', 'Total Followers', 'Followers Growth',
  'Avg Engagement', 'Page Reach', 'Earned Media Value'];

const FB_KPI_LABELS = {
  'Total Posts':        'Total Posts',
  'Total Engagement':   'Interacciones',
  'Total Followers':    'Seguidores',
  'Followers Growth':   'Crecimiento',
  'Avg Engagement':     'Avg. Interacciones',
  'Page Reach':         'Alcance',
  'Earned Media Value': 'Valor Medios Ganados',
};

function FacebookDashboard({ r }) {
  return (
    <div style={S.content} className="rp-content">
      {/* KPIs */}
      <div data-pdf-block="true" style={S.kpiGrid} className="rp-kpi-grid">
        {r.kpis.filter(k => FB_HERO.includes(k.kpi)).map((k, i) =>
          <KpiCard key={i} {...k} kpi={FB_KPI_LABELS[k.kpi] || k.kpi} accent={FB_BLUE} />)}
      </div>

      {/* Reach + Views */}
      <div style={S.row} className="rp-row">
        <Card flex={1}><SectionTitle>Alcance diario</SectionTitle><TimeChart data={r.reachDist} label="Reach" color={FB_BLUE} /></Card>
        <Card flex={1}><SectionTitle>Vistas diarias</SectionTitle><TimeChart data={r.viewsDist} label="Views" color="#42B3F5" /></Card>
      </div>

      {/* Interacciones + Posts */}
      <div style={S.row} className="rp-row">
        <Card flex={1}><SectionTitle>Interacciones diarias</SectionTitle><TimeChart data={r.engagementDist} label="Interacciones" color="#F06000" /></Card>
        <Card flex={1}>
          <SectionTitle>Posts por día</SectionTitle>
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={r.postsDist} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" tickFormatter={shortDay} tick={{ fontSize: 10 }} interval={Math.floor(r.postsDist.length / 7)} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip labelFormatter={shortDay} formatter={v => [v, 'Posts']} />
              <Bar dataKey="value" fill={FB_BLUE} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Followers */}
      {r.followersDist.length > 0 && (
        <Card><SectionTitle>Evolución de seguidores</SectionTitle><TimeChart data={r.followersDist} label="Seguidores" color="#0D47A1" autoScale={true} /></Card>
      )}

      {/* Top Posts */}
      {r.topPosts.length > 0 && (
        <Card>
          <SectionTitle>Top Posts</SectionTitle>
          <div style={S.postList}>
            {r.topPosts.map((p, i) => <PostCard key={i} post={p} idx={i} accent={FB_BLUE} platform="facebook" />)}
          </div>
        </Card>
      )}

      {/* Post types + Interacciones por tipo + Países en una sola fila */}
      <div style={S.row} className="rp-row">
        {r.postTypes.length > 0 && (
          <Card flex="0 0 200px" className="rp-card-fixed">
            <SectionTitle>Tipos de post</SectionTitle>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Tipo</th><th style={S.th}>Cant.</th><th style={S.th}>%</th></tr></thead>
              <tbody>{r.postTypes.map((t, i) => (
                <tr key={i} style={i % 2 === 0 ? S.trEven : {}}>
                  <td style={S.td}><span style={{ ...S.tag, background: '#e8f0fe', color: FB_BLUE }}>{t.type}</span></td>
                  <td style={S.tdNum}>{t.count}</td>
                  <td style={S.tdNum}>{t.pct}%</td>
                </tr>
              ))}</tbody>
            </table>
          </Card>
        )}
        {r.postTypesByEngagement.length > 0 && (
          <Card flex={1}>
            <SectionTitle>Interacciones por tipo</SectionTitle>
            <HBarChart data={r.postTypesByEngagement} color={FB_BLUE} label="Interacciones" />
          </Card>
        )}
        {r.countries.length > 0 && (
          <Card flex="0 0 260px" className="rp-card-fixed">
            <SectionTitle>Países</SectionTitle>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart><Pie data={r.countries.slice(0, 3)} dataKey="count" nameKey="name"
                cx="50%" cy="50%" outerRadius={65}
                label={makePieLabel(({ name, payload }) => `${(payload.name || name).split(',')[0]} ${payload.pct}%`)}
                labelLine={false}>
                {r.countries.slice(0, 3).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie><Tooltip formatter={(v, n) => [fmt(v), n]} /></PieChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      {/* Top ciudades */}
      {r.cities.length > 0 && (
        <Card>
          <SectionTitle>Top ciudades</SectionTitle>
          <HBarChart
            data={r.cities.slice(0, 10).map(c => ({ ...c, type: c.name.split(',')[0] }))}
            dataKey="count" nameKey="type" color={FB_BLUE} label="Seguidores" height={240} />
        </Card>
      )}

      {/* KPI Evolution table */}
      {r.kpiEvolution.length > 0 && (
        <Card>
          <SectionTitle>Evolución diaria de KPIs</SectionTitle>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>
                {['Fecha','Posts','Interacciones','Reactions','Comments','Shares','Clicks'].map(h =>
                  <th key={h} style={S.th}>{h}</th>)}
              </tr></thead>
              <tbody>{r.kpiEvolution.map((row, i) => (
                <tr key={i} style={i % 2 === 0 ? S.trEven : {}}>
                  <td style={S.td}>{row.date}</td>
                  <td style={S.tdNum}>{row.posts || 0}</td>
                  <td style={{ ...S.tdNum, fontWeight: row.engagement > 0 ? 600 : 400, color: row.engagement > 0 ? FB_BLUE : 'inherit' }}>{fmt(row.engagement)}</td>
                  <td style={S.tdNum}>{fmt(row.reactions)}</td>
                  <td style={S.tdNum}>{fmt(row.comments)}</td>
                  <td style={S.tdNum}>{fmt(row.shares)}</td>
                  <td style={S.tdNum}>{fmt(row.clicks)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </Card>
      )}

      {/* All KPIs */}
      <AllKpisTable kpis={r.kpis.map(k => ({ ...k, kpi: FB_KPI_LABELS[k.kpi] || k.kpi }))} accent={FB_BLUE} />
    </div>
  );
}

/* ─── Instagram dashboard ────────────────────────────────────────────── */
const IG_HERO = ['Total Organic Posts', 'Total Organic Engagement', 'Total Followers',
  'Followers Growth', 'Avg. Engagement', 'Total Posts Views', 'Engagement Rate By Reach', 'Earned Media Value'];

const IG_KPI_LABELS = {
  'Total Organic Posts':          'Total Posts',
  'Total Organic Engagement':     'Interacciones',
  'Total Followers':              'Seguidores',
  'Followers Growth':             'Crecimiento',
  'Avg. Engagement':              'Avg. Interacciones',
  'Total Posts Views':            'Vistas',
  'Engagement Rate By Reach':     'Tasa de Interacciones',
  'Earned Media Value':           'Valor Medios Ganados',
};

function InstagramDashboard({ r }) {
  const accent = IG_GRAD1;
  return (
    <div style={S.content} className="rp-content">
      {/* KPIs */}
      <div data-pdf-block="true" style={S.kpiGrid} className="rp-kpi-grid">
        {r.kpis.filter(k => IG_HERO.includes(k.kpi)).map((k, i) =>
          <KpiCard key={i} {...k} kpi={IG_KPI_LABELS[k.kpi] || k.kpi} accent={[IG_GRAD1, IG_GRAD2, IG_ORANGE, '#0D47A1', IG_GRAD1, IG_GRAD2, IG_ORANGE, IG_GRAD1][i % 8]} />)}
      </div>

      {/* Followers + Growth */}
      <div style={S.row} className="rp-row">
        <Card flex={1}><SectionTitle>Seguidores por día</SectionTitle><TimeChart data={r.followerCountDist} label="Seguidores" color={accent} autoScale={true} /></Card>
        <Card flex={1}><SectionTitle>Crecimiento diario</SectionTitle><TimeChart data={r.followerGrowthDist} label="Crecimiento" color={IG_GRAD2} /></Card>
      </div>

      {/* Profile visits + Interacciones */}
      <div style={S.row} className="rp-row">
        <Card flex={1}><SectionTitle>Visitas al perfil</SectionTitle><TimeChart data={r.profileVisitsDist} label="Visitas" color={IG_ORANGE} /></Card>
        <Card flex={1}><SectionTitle>Interacciones diarias</SectionTitle><TimeChart data={r.engagementDist} label="Interacciones" color={IG_GRAD1} /></Card>
      </div>

      {/* Likes + Posts */}
      <div style={S.row} className="rp-row">
        <Card flex={1}><SectionTitle>Likes diarios</SectionTitle><TimeChart data={r.likesDist} label="Likes" color={IG_GRAD2} /></Card>
        <Card flex={1}>
          <SectionTitle>Posts por día</SectionTitle>
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={r.postsDist} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" tickFormatter={shortDay} tick={{ fontSize: 10 }} interval={Math.floor(r.postsDist.length / 7)} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip labelFormatter={shortDay} formatter={v => [v, 'Posts']} />
              <Bar dataKey="value" fill={accent} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Top Posts */}
      {r.topPosts.length > 0 && (
        <Card>
          <SectionTitle>Top Posts</SectionTitle>
          <div style={S.postList}>
            {r.topPosts.map((p, i) => <PostCard key={i} post={p} idx={i} accent={accent} platform="instagram" />)}
          </div>
        </Card>
      )}

      {/* Top Stories */}
      {r.topStories.length > 0 && (
        <Card>
          <SectionTitle>Top Stories</SectionTitle>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>
                {['Fecha','Tipo','Reach','Views','Replies','Taps adelante','Taps atrás','Salidas'].map(h => <th key={h} style={S.th}>{h}</th>)}
              </tr></thead>
              <tbody>{r.topStories.map((s, i) => (
                <tr key={i} style={i % 2 === 0 ? S.trEven : {}}>
                  <td style={S.td}>{s.date}</td>
                  <td style={S.td}><span style={{ ...S.tag, background: '#f3e5f5', color: IG_GRAD1 }}>{s.type}</span></td>
                  <td style={S.tdNum}>{fmt(s.reach)}</td>
                  <td style={S.tdNum}>{fmt(s.views)}</td>
                  <td style={S.tdNum}>{fmt(s.replies)}</td>
                  <td style={S.tdNum}>{fmt(s.tapsFwd)}</td>
                  <td style={S.tdNum}>{fmt(s.tapsBwd)}</td>
                  <td style={S.tdNum}>{fmt(s.exits)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Post types + Hashtags */}
      <div style={S.row} className="rp-row">
        <Card flex={1}>
          <SectionTitle>Tipos de post</SectionTitle>
          {r.postTypes.length > 0 && (
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={r.postTypes} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={55}
                  label={makePieLabel(({ payload }) => `${payload.type} (${payload.count})`)}
                  labelLine={false}>
                  {r.postTypes.map((_, i) => <Cell key={i} fill={[IG_GRAD1, IG_GRAD2, IG_ORANGE][i % 3]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
          {r.postTypesByEngagement.length > 0 && <>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginTop: 10, marginBottom: 4 }}>Interacciones por tipo</div>
            <HBarChart data={r.postTypesByEngagement} color={IG_GRAD1} label="Interacciones" height={120} />
          </>}
        </Card>
        {r.hashtags.length > 0 && (
          <Card flex={1}>
            <SectionTitle>Top Hashtags</SectionTitle>
            <HBarChart data={r.hashtags.slice(0, 10)} nameKey="tag" color={IG_GRAD2} label="Avg. Interacciones" height={240} />
          </Card>
        )}
      </div>

      {/* Gender */}
      <div style={S.row} className="rp-row">
        {r.genders.length > 0 && (
          <Card flex="0 0 280px" className="rp-card-fixed">
            <SectionTitle>Género</SectionTitle>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={r.genders} dataKey="count" nameKey="gender" cx="50%" cy="50%" outerRadius={60}
                  label={makePieLabel(({ payload }) => `${payload.gender} ${payload.pct}%`)}
                  labelLine={false}>
                  {r.genders.map((_, i) => <Cell key={i} fill={[IG_GRAD1, IG_GRAD2, IG_ORANGE, '#90CAF9'][i % 4]} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [fmt(v), n]} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        )}
        {r.genderAge.length > 0 && (
          <Card flex={1}>
            <SectionTitle>Distribución por edad y género</SectionTitle>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={r.genderAge} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="age" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={fmt} />
                <Tooltip formatter={fmt} />
                <Legend />
                <Bar dataKey="male"   name="Hombre" fill={IG_GRAD1} radius={[3, 3, 0, 0]} />
                <Bar dataKey="female" name="Mujer"  fill={IG_GRAD2} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      {/* Geography */}
      <div style={S.row} className="rp-row">
        {r.countries.length > 0 && (
          <Card flex="0 0 320px" className="rp-card-fixed">
            <SectionTitle>Países</SectionTitle>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart><Pie data={r.countries.slice(0, 3)} dataKey="count" nameKey="name"
                cx="50%" cy="50%" outerRadius={70}
                label={makePieLabel(({ payload }) => `${(payload.name || '').split(',')[0]} ${payload.pct}%`)}
                labelLine={false}>
                {r.countries.slice(0, 3).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie><Tooltip formatter={(v, n) => [fmt(v), n]} /></PieChart>
            </ResponsiveContainer>
          </Card>
        )}
        {r.cities.length > 0 && (
          <Card flex={1}>
            <SectionTitle>Top ciudades</SectionTitle>
            <HBarChart
              data={r.cities.slice(0, 10).map(c => ({ ...c, type: c.name.split(',')[0] }))}
              dataKey="count" nameKey="type" color={IG_GRAD1} label="Seguidores" height={240} />
          </Card>
        )}
      </div>

      {/* Heatmap */}
      {r.engHeatmap && r.engHeatmap.rows && r.engHeatmap.rows.length > 0 && (
        <Card>
          <SectionTitle>Interacciones por día y hora</SectionTitle>
          <EngHeatmap data={r.engHeatmap} />
        </Card>
      )}

      {/* KPI Evolution */}
      {r.kpiEvolution.length > 0 && (
        <Card>
          <SectionTitle>Evolución diaria de KPIs</SectionTitle>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>
                {['Fecha','Posts','Interacciones','Likes','Comments','Saves','Video Views','Reach','Impressions'].map(h =>
                  <th key={h} style={S.th}>{h}</th>)}
              </tr></thead>
              <tbody>{r.kpiEvolution.map((row, i) => (
                <tr key={i} style={i % 2 === 0 ? S.trEven : {}}>
                  <td style={S.td}>{row.date}</td>
                  <td style={S.tdNum}>{row.posts || 0}</td>
                  <td style={{ ...S.tdNum, fontWeight: row.engagement > 0 ? 600 : 400, color: row.engagement > 0 ? IG_GRAD1 : 'inherit' }}>{fmt(row.engagement)}</td>
                  <td style={S.tdNum}>{fmt(row.likes)}</td>
                  <td style={S.tdNum}>{fmt(row.comments)}</td>
                  <td style={S.tdNum}>{fmt(row.saves)}</td>
                  <td style={S.tdNum}>{fmt(row.videoViews)}</td>
                  <td style={S.tdNum}>{fmt(row.reach)}</td>
                  <td style={S.tdNum}>{fmt(row.impressions)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </Card>
      )}

      <AllKpisTable kpis={r.kpis.map(k => ({ ...k, kpi: IG_KPI_LABELS[k.kpi] || k.kpi }))} accent={IG_GRAD1} />
    </div>
  );
}

/* ─── AllKpisTable ───────────────────────────────────────────────────── */
function AllKpisTable({ kpis, accent }) {
  if (!kpis || kpis.length === 0) return null;
  return (
    <Card>
      <SectionTitle>Todos los KPIs</SectionTitle>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Métrica</th><th style={S.th}>Actual</th>
            <th style={S.th}>Anterior</th><th style={S.th}>Cambio %</th>
          </tr></thead>
          <tbody>{kpis.map((k, i) => (
            <tr key={i} style={i % 2 === 0 ? S.trEven : {}}>
              <td style={S.td}>{k.kpi}</td>
              <td style={{ ...S.tdNum, fontWeight: 600, color: accent }}>{fmt(k.curr)}</td>
              <td style={S.tdNum}>{fmt(k.prev)}</td>
              <td style={{ ...S.tdNum, color: typeof k.pct === 'number' && k.pct > 0 ? '#2e7d32' : typeof k.pct === 'number' && k.pct < 0 ? '#c62828' : '#666', fontWeight: 600 }}>
                {typeof k.pct === 'number' && k.pct !== 0 ? `${k.pct > 0 ? '+' : ''}${k.pct}%` : '—'}
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </Card>
  );
}

/* ─── TikTok dashboard ───────────────────────────────────────────────── */
const TK_HERO = ['Total Posts', 'Total Engagement', 'Follower Count', 'Followers Growth',
  'Avg. Engagement', 'Avg. Engagement Rate By Followers', 'Earned Media Value'];

const TK_KPI_LABELS = {
  'Total Posts':                        'Total Posts',
  'Total Engagement':                   'Interacciones',
  'Follower Count':                     'Seguidores',
  'Followers Growth':                   'Crecimiento',
  'Avg. Engagement':                    'Avg. Interacciones',
  'Avg. Engagement Rate By Followers':  'Tasa de Interacciones',
  'Earned Media Value':                 'Valor Medios Ganados',
};

function TikTokPostCard({ post, idx }) {
  const [exp, setExp] = useState(false);
  const msg = post.caption || '';
  return (
    <div style={S.postCard}>
      <div style={{ ...S.postRank, color: TK_RED }}>#{idx + 1}</div>
      <div style={{ flex: 1 }}>
        <div style={S.postMeta}>
          <span style={{ ...S.postType, background: TK_RED }}>{post.type || 'video'}</span>
          <span style={S.postDate}>{post.date}</span>
          {post.link && (
            <a href={post.link} target="_blank" rel="noopener noreferrer" style={{ ...S.postLink, color: TK_RED }}>
              Ver en TikTok ↗
            </a>
          )}
        </div>
        {msg && (
          <div style={S.postMessage} onClick={() => setExp(!exp)}>
            {exp ? msg : msg.slice(0, 140) + (msg.length > 140 ? '… ' : '')}
            {!exp && msg.length > 140 && <span style={{ color: TK_RED, fontWeight: 600, fontSize: 12 }}>ver más</span>}
          </div>
        )}
        <div style={S.postStats}>
          <Stat label="Views"       value={fmt(post.views)} bold />
          <Stat label="Likes"       value={fmt(post.likes)} />
          <Stat label="Comments"    value={fmt(post.comments)} />
          <Stat label="Interacciones"  value={fmt(post.engagement)} />
          <Stat label="Eng. Rate"   value={post.engRate ? String(post.engRate).slice(0, 6) + '%' : '—'} />
          <Stat label="Hashtags"    value={post.hashtagCount || '—'} />
        </div>
      </div>
    </div>
  );
}

function TikTokDashboard({ r }) {
  return (
    <div style={S.content} className="rp-content">
      {/* KPIs */}
      <div data-pdf-block="true" style={S.kpiGrid} className="rp-kpi-grid">
        {r.kpis.filter(k => TK_HERO.includes(k.kpi)).map((k, i) =>
          <KpiCard key={i} {...k} kpi={TK_KPI_LABELS[k.kpi] || k.kpi} accent={[TK_RED, TK_TEAL, TK_RED, TK_TEAL, TK_RED, TK_TEAL, TK_RED][i % 7]} />)}
      </div>

      {/* Views + Interacciones */}
      <div style={S.row} className="rp-row">
        <Card flex={1}><SectionTitle>Vistas diarias</SectionTitle><TimeChart data={r.viewsDist} label="Vistas" color={TK_RED} /></Card>
        <Card flex={1}><SectionTitle>Interacciones diarias</SectionTitle><TimeChart data={r.engagementDist} label="Interacciones" color={TK_TEAL} /></Card>
      </div>

      {/* Likes + Comments */}
      <div style={S.row} className="rp-row">
        <Card flex={1}><SectionTitle>Likes diarios</SectionTitle><TimeChart data={r.likesDist} label="Likes" color={TK_RED} /></Card>
        <Card flex={1}><SectionTitle>Shares diarios</SectionTitle><TimeChart data={r.sharesDist} label="Shares" color={TK_TEAL} /></Card>
      </div>

      {/* Saves + Posts */}
      <div style={S.row} className="rp-row">
        <Card flex={1}><SectionTitle>Guardados diarios</SectionTitle><TimeChart data={r.savesDist} label="Saves" color="#6C3483" /></Card>
        <Card flex={1}>
          <SectionTitle>Posts por día</SectionTitle>
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={r.postsDist} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" tickFormatter={shortDay} tick={{ fontSize: 10 }} interval={Math.max(0, Math.floor(r.postsDist.length / 7) - 1)} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip labelFormatter={shortDay} formatter={v => [v, 'Posts']} />
              <Bar dataKey="value" fill={TK_RED} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Top Posts */}
      {r.topPosts.length > 0 && (
        <Card>
          <SectionTitle>Top Posts</SectionTitle>
          <div style={S.postList}>
            {r.topPosts.map((p, i) => <TikTokPostCard key={i} post={p} idx={i} />)}
          </div>
        </Card>
      )}

      {/* Hashtags */}
      <div style={S.row} className="rp-row">
        {r.hashtagsByUsage.length > 0 && (
          <Card flex={1}>
            <SectionTitle>Hashtags más usados</SectionTitle>
            <HBarChart data={r.hashtagsByUsage.slice(0, 10)} nameKey="tag" dataKey="count"
              color={TK_TEAL} label="Usos" height={240} />
          </Card>
        )}
        {r.hashtagsByEng.length > 0 && (
          <Card flex={1}>
            <SectionTitle>Hashtags con mayor interacción</SectionTitle>
            <HBarChart data={r.hashtagsByEng.slice(0, 10)} nameKey="tag"
              color={TK_RED} label="Avg. Interacciones" height={240} />
          </Card>
        )}
      </div>

      {/* Heatmap */}
      {r.engHeatmap && r.engHeatmap.rows && r.engHeatmap.rows.length > 0 && (
        <Card>
          <SectionTitle>Interacciones por día y hora</SectionTitle>
          <EngHeatmap data={r.engHeatmap} color="238,29,82" />
        </Card>
      )}

      {/* All KPIs */}
      <AllKpisTable kpis={r.kpis.map(k => ({ ...k, kpi: TK_KPI_LABELS[k.kpi] || k.kpi }))} accent={TK_RED} />
    </div>
  );
}

/* ─── Resumen dashboard ──────────────────────────────────────────────── */
function ResumenDashboard({ reports }) {
  const fb = reports.facebook;
  const ig = reports.instagram;
  const tk = reports.tiktok;

  function kpiVal(r, ...names) {
    if (!r || !r.kpis) return null;
    for (const name of names) {
      const k = r.kpis.find(k => k.kpi === name);
      if (k && k.curr !== null && k.curr !== undefined) return k.curr;
    }
    return null;
  }
  function kpiChange(r, ...names) {
    if (!r || !r.kpis) return null;
    for (const name of names) {
      const k = r.kpis.find(k => k.kpi === name);
      if (k) return k.pct;
    }
    return null;
  }
  const safeNum = v => (typeof v === 'number' ? v : 0);

  const fbFollowers  = safeNum(kpiVal(fb, 'Total Followers'));
  const igFollowers  = safeNum(kpiVal(ig, 'Total Followers'));
  const tkFollowers  = safeNum(kpiVal(tk, 'Follower Count'));
  const fbEngagement = safeNum(kpiVal(fb, 'Total Engagement'));
  const igEngagement = safeNum(kpiVal(ig, 'Total Organic Engagement'));
  const tkEngagement = safeNum(kpiVal(tk, 'Total Engagement'));
  const fbPosts      = safeNum(kpiVal(fb, 'Total Posts'));
  const igPosts      = safeNum(kpiVal(ig, 'Total Organic Posts'));
  const tkPosts      = safeNum(kpiVal(tk, 'Total Posts'));
  const fbEMV        = safeNum(kpiVal(fb, 'Earned Media Value'));
  const igEMV        = safeNum(kpiVal(ig, 'Earned Media Value'));
  const tkEMV        = safeNum(kpiVal(tk, 'Earned Media Value'));
  const fbReach      = safeNum(kpiVal(fb, 'Page Reach'));
  const igViews      = safeNum(kpiVal(ig, 'Total Posts Views'));
  const tkViews      = safeNum(kpiVal(tk, 'Total Views'));

  const totalFollowers  = fbFollowers + igFollowers + tkFollowers;
  const totalEngagement = fbEngagement + igEngagement + tkEngagement;
  const totalPosts      = fbPosts + igPosts + tkPosts;
  const totalEMV        = fbEMV + igEMV + tkEMV;
  const totalReach      = fbReach + igViews + tkViews;

  const engData = [
    ...(fb ? [{ plataforma: 'Facebook',  valor: fbEngagement, fill: FB_BLUE  }] : []),
    ...(ig ? [{ plataforma: 'Instagram', valor: igEngagement, fill: IG_GRAD1 }] : []),
    ...(tk ? [{ plataforma: 'TikTok',    valor: tkEngagement, fill: TK_RED   }] : []),
  ];
  const folData = [
    ...(fb ? [{ plataforma: 'Facebook',  valor: fbFollowers, fill: FB_BLUE  }] : []),
    ...(ig ? [{ plataforma: 'Instagram', valor: igFollowers, fill: IG_GRAD1 }] : []),
    ...(tk ? [{ plataforma: 'TikTok',    valor: tkFollowers, fill: TK_RED   }] : []),
  ];
  const emvData = [
    ...(fb && fbEMV ? [{ plataforma: 'Facebook',  valor: fbEMV, fill: FB_BLUE  }] : []),
    ...(ig && igEMV ? [{ plataforma: 'Instagram', valor: igEMV, fill: IG_GRAD1 }] : []),
    ...(tk && tkEMV ? [{ plataforma: 'TikTok',    valor: tkEMV, fill: TK_RED   }] : []),
  ];

  const platforms = [
    fb && {
      key: 'facebook', label: 'Facebook', color: FB_BLUE,
      iconBg: FB_BLUE, icon: 'f',
      dateRange: fb.dateRange, profile: fb.profile,
      metrics: [
        { label: 'Seguidores',          value: fbFollowers,                      pct: kpiChange(fb, 'Total Followers') },
        { label: 'Crecimiento',         value: kpiVal(fb, 'Followers Growth'),    pct: kpiChange(fb, 'Followers Growth') },
        { label: 'Interacciones',       value: fbEngagement,                     pct: kpiChange(fb, 'Total Engagement') },
        { label: 'Alcance',             value: kpiVal(fb, 'Page Reach'),          pct: kpiChange(fb, 'Page Reach') },
        { label: 'Posts',               value: fbPosts,                          pct: kpiChange(fb, 'Total Posts') },
        { label: 'Avg. Interacciones',  value: kpiVal(fb, 'Avg Engagement'),      pct: kpiChange(fb, 'Avg Engagement') },
        { label: 'EMV',                 value: fbEMV || null,                    pct: kpiChange(fb, 'Earned Media Value') },
      ],
    },
    ig && {
      key: 'instagram', label: 'Instagram', color: IG_GRAD1,
      iconBg: 'linear-gradient(45deg,#833AB4,#E1306C,#F77737)', icon: 'IG',
      dateRange: ig.dateRange, profile: ig.profile,
      metrics: [
        { label: 'Seguidores',          value: igFollowers,                                   pct: kpiChange(ig, 'Total Followers') },
        { label: 'Crecimiento',         value: kpiVal(ig, 'Followers Growth'),                pct: kpiChange(ig, 'Followers Growth') },
        { label: 'Interacciones',       value: igEngagement,                                  pct: kpiChange(ig, 'Total Organic Engagement') },
        { label: 'Vistas',              value: kpiVal(ig, 'Total Posts Views'),               pct: kpiChange(ig, 'Total Posts Views') },
        { label: 'Posts',               value: igPosts,                                       pct: kpiChange(ig, 'Total Organic Posts') },
        { label: 'Avg. Interacciones',  value: kpiVal(ig, 'Avg. Engagement'),                 pct: kpiChange(ig, 'Avg. Engagement') },
        { label: 'EMV',                 value: igEMV || null,                                 pct: kpiChange(ig, 'Earned Media Value') },
      ],
    },
    tk && {
      key: 'tiktok', label: 'TikTok', color: TK_RED,
      iconBg: '#010101', icon: 'TK',
      dateRange: tk.dateRange, profile: tk.profile,
      metrics: [
        { label: 'Seguidores',          value: tkFollowers,                               pct: kpiChange(tk, 'Follower Count') },
        { label: 'Crecimiento',         value: kpiVal(tk, 'Followers Growth'),            pct: kpiChange(tk, 'Followers Growth') },
        { label: 'Interacciones',       value: tkEngagement,                              pct: kpiChange(tk, 'Total Engagement') },
        { label: 'Vistas',              value: kpiVal(tk, 'Total Views'),                 pct: kpiChange(tk, 'Total Views') },
        { label: 'Posts',               value: tkPosts,                                   pct: kpiChange(tk, 'Total Posts') },
        { label: 'Avg. Interacciones',  value: kpiVal(tk, 'Avg. Engagement'),             pct: kpiChange(tk, 'Avg. Engagement') },
        { label: 'EMV',                 value: tkEMV || null,                             pct: kpiChange(tk, 'Earned Media Value') },
      ],
    },
  ].filter(Boolean);

  const fbReachOnly = safeNum(kpiVal(fb, 'Page Reach'));
  const igViewsOnly = safeNum(kpiVal(ig, 'Total Posts Views'));
  const tkViewsOnly = safeNum(kpiVal(tk, 'Total Views'));
  const totalAlcance = fbReachOnly;
  const totalVistas  = igViewsOnly + tkViewsOnly;

  const TOTALES = [
    { label: 'Comunidad Total',       value: totalFollowers,  accent: '#4A4A8A', sub: 'seguidores combinados' },
    { label: 'Interacciones Total',   value: totalEngagement, accent: '#D4500A', sub: 'interacciones combinadas' },
    { label: 'Alcance',               value: totalAlcance,    accent: '#167A52', sub: 'alcance combinado' },
    { label: 'Vistas',                value: totalVistas,     accent: '#00838F', sub: 'vistas combinadas' },
    { label: 'Posts Totales',         value: totalPosts,      accent: '#0277BD', sub: 'publicaciones combinadas' },
    { label: 'EMV Total',             value: totalEMV,        accent: '#7B3FAD', sub: 'valor medios ganados' },
  ].filter(t => t.value > 0);

  return (
    <div style={S.content} className="rp-content">
      {/* ── Totales consolidados ── */}
      <div data-pdf-block="true" className="rp-kpi-grid rp-totales" style={{ ...S.kpiGrid, gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))' }}>
        {TOTALES.map((t, i) => (
          <div key={i} style={{ ...S.kpiCard, borderTop: `3px solid ${t.accent}` }}>
            <div style={S.kpiLabel}>{t.label}</div>
            <div style={{ ...S.kpiValue, color: t.accent }}>{fmt(t.value)}</div>
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{t.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Gráficos comparativos ── */}
      <div style={S.row} className="rp-row">
        <Card flex={1}>
          <SectionTitle>Interacciones por plataforma</SectionTitle>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={engData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="plataforma" tick={{ fontSize: 13, fontWeight: 600 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={fmt} />
              <Tooltip formatter={v => [fmt(v), 'Interacciones']} />
              <Bar dataKey="valor" radius={[6, 6, 0, 0]}>
                {engData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card flex={1}>
          <SectionTitle>Seguidores por plataforma</SectionTitle>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={folData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="plataforma" tick={{ fontSize: 13, fontWeight: 600 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={fmt} />
              <Tooltip formatter={v => [fmt(v), 'Seguidores']} />
              <Bar dataKey="valor" radius={[6, 6, 0, 0]}>
                {folData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        {emvData.length > 1 && (
          <Card flex={1}>
            <SectionTitle>EMV por plataforma</SectionTitle>
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={emvData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="plataforma" tick={{ fontSize: 13, fontWeight: 600 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={fmt} />
                <Tooltip formatter={v => [fmt(v), 'EMV']} />
                <Bar dataKey="valor" radius={[6, 6, 0, 0]}>
                  {emvData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      {/* ── Tarjetas por plataforma ── */}
      <div data-pdf-block="true" className="rp-plat-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${platforms.length}, 1fr)`, gap: 16 }}>
        {platforms.map(plat => (
          <div key={plat.key} style={{
            background: '#fff', border: '1px solid #e8edf5', borderRadius: 12,
            padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,.06)',
            borderTop: `4px solid ${plat.color}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <span style={{ ...S.platIcon, background: plat.iconBg, width: 28, height: 28, fontSize: plat.icon === 'TK' ? 9 : 12 }}>
                {plat.icon}
              </span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: plat.color }}>{plat.label}</div>
                {plat.profile && <div style={{ fontSize: 11, color: '#888' }}>{plat.profile}</div>}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {plat.metrics.filter(m => m.value !== null && m.value !== undefined).map((m, i) => {
                const up   = typeof m.pct === 'number' && m.pct > 0;
                const down = typeof m.pct === 'number' && m.pct < 0;
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f4f4f4' }}>
                    <span style={{ fontSize: 12, color: '#777' }}>{m.label}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>{fmt(m.value)}</span>
                      {m.pct !== null && m.pct !== 0 && typeof m.pct === 'number' && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: up ? '#2e7d32' : '#c62828' }}>
                          {up ? '▲' : '▼'}{Math.abs(m.pct)}%
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {plat.dateRange && (
              <div style={{ fontSize: 10, color: '#bbb', marginTop: 10 }}>{plat.dateRange}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── CompetenciaPlatDashboard ───────────────────────────────────────── */
const COMP_KPI_LABELS = {
  // Instagram
  'Total Organic Posts':              'Total Posts',
  'Total Organic Engagement':         'Interacciones',
  'Total Followers':                  'Seguidores',
  'Followers Growth':                 'Crecimiento',
  'Avg. Engagement':                  'Avg. Interacciones',
  'Total Posts Views':                'Vistas',
  'Engagement Rate By Reach':         'Tasa de Interacciones',
  'Earned Media Value':               'Valor Medios Ganados',
  // Facebook
  'Total Posts':                      'Total Posts',
  'Total Engagement':                 'Interacciones',
  'Avg Engagement':                   'Avg. Interacciones',
  'Page Reach':                       'Alcance',
  // TikTok
  'Follower Count':                   'Seguidores',
  'Avg. Engagement Rate By Followers':'Tasa de Interacciones',
  // Generic fallbacks
  'Engagement':                       'Interacciones',
  'Avg. Engagement Rate':             'Tasa de Interacciones',
};

function CompetenciaPlatDashboard({ r, plat }) {
  if (!plat || !r) return null;
  const { color, key, icon, iconBg } = plat;
  const heroKpis = r.kpis ? r.kpis.slice(0, 6) : [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Profile header */}
      {r.profile && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
          background: '#fff', borderRadius: 10, border: '1px solid #e8edf5', boxShadow: '0 1px 4px rgba(0,0,0,.04)' }}>
          <span style={{ ...S.platIcon, background: iconBg, width: 34, height: 34, fontSize: key === 'tiktok' ? 9 : 12 }}>{icon}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a' }}>{r.profile}</div>
            {r.dateRange && <div style={{ fontSize: 11, color: '#888' }}>{r.dateRange}</div>}
          </div>
          <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: '#DC2626',
            background: '#fef2f2', borderRadius: 20, padding: '3px 10px', letterSpacing: '.4px', textTransform: 'uppercase' }}>
            COMPETENCIA
          </span>
        </div>
      )}

      {/* KPI cards */}
      {heroKpis.length > 0 && (
        <div data-pdf-block="true" style={S.kpiGrid} className="rp-kpi-grid">
          {heroKpis.map((k, i) => <KpiCard key={i} {...k} kpi={COMP_KPI_LABELS[k.kpi] || k.kpi} accent={color} />)}
        </div>
      )}

      {/* Engagement + Posts */}
      {(r.engagementDist?.length > 0 || r.postsDist?.length > 0) && (
        <div style={S.row} className="rp-row">
          {r.engagementDist?.length > 0 && (
            <Card flex={1}><SectionTitle>Interacciones diarias</SectionTitle>
              <TimeChart data={r.engagementDist} label="Interacciones" color={color} /></Card>
          )}
          {r.postsDist?.length > 0 && (
            <Card flex={1}>
              <SectionTitle>Posts por día</SectionTitle>
              <ResponsiveContainer width="100%" height={190}>
                <BarChart data={r.postsDist} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="day" tickFormatter={shortDay} tick={{ fontSize: 10 }}
                    interval={Math.max(0, Math.floor(r.postsDist.length / 7) - 1)} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip labelFormatter={shortDay} formatter={v => [v, 'Posts']} />
                  <Bar dataKey="value" fill={color} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
        </div>
      )}

      {/* Followers / Views / Reach */}
      {(r.followerCountDist?.length > 0 || r.followersDist?.length > 0 ||
        r.viewsDist?.length > 0 || r.likesDist?.length > 0) && (
        <div style={S.row} className="rp-row">
          {(r.followerCountDist?.length > 0 || r.followersDist?.length > 0) && (
            <Card flex={1}><SectionTitle>Seguidores</SectionTitle>
              <TimeChart
                data={r.followerCountDist?.length > 0 ? r.followerCountDist : r.followersDist}
                label="Seguidores" color={color} autoScale /></Card>
          )}
          {r.viewsDist?.length > 0 && (
            <Card flex={1}><SectionTitle>Vistas diarias</SectionTitle>
              <TimeChart data={r.viewsDist} label="Vistas" color={color} /></Card>
          )}
          {r.likesDist?.length > 0 && !r.viewsDist?.length && (
            <Card flex={1}><SectionTitle>Likes diarios</SectionTitle>
              <TimeChart data={r.likesDist} label="Likes" color={color} /></Card>
          )}
        </div>
      )}

      {/* Top Posts */}
      {r.topPosts?.length > 0 && (
        <Card>
          <SectionTitle>Top Posts</SectionTitle>
          <div style={S.postList}>
            {r.topPosts.map((p, i) => {
              if (key === 'tiktok')   return <TikTokPostCard key={i} post={p} idx={i} />;
              if (key === 'facebook') return <PostCard key={i} post={p} idx={i} accent={color} platform="facebook" />;
              return <CompetenciaPostCard key={i} post={p} idx={i} accent={color} />;
            })}
          </div>
        </Card>
      )}

      {/* Post types */}
      {r.postTypes?.length > 0 && (
        <Card>
          <SectionTitle>Tipos de post</SectionTitle>
          <HBarChart data={r.postTypes} dataKey="count" nameKey="type" color={color} label="Publicaciones" height={120} />
        </Card>
      )}

      {/* All KPIs */}
      {r.kpis?.length > 0 && <AllKpisTable kpis={r.kpis} accent={color} />}
    </div>
  );
}

/* ─── CompetenciaDashboard (multi-brand) ────────────────────────────── */
const COMP_PLATS = [
  { key: 'instagram', label: 'Instagram', color: IG_GRAD1, icon: 'IG', iconBg: 'linear-gradient(45deg, #833AB4, #E1306C, #F77737)' },
  { key: 'facebook',  label: 'Facebook',  color: FB_BLUE,  icon: 'f',  iconBg: FB_BLUE },
  { key: 'tiktok',    label: 'TikTok',    color: TK_RED,   icon: 'TK', iconBg: '#010101' },
];

/* Panel to add a new brand — brand name input + 3 upload buttons */
function NewBrandPanel({ onUpload, compReports, onGoToBrand }) {
  const [brandName, setBrandName] = useState('');
  const [uploaded, setUploaded] = useState({}); // { instagram: true, ... }
  const igRef = useRef();
  const fbRef = useRef();
  const tkRef = useRef();
  const refs = { instagram: igRef, facebook: fbRef, tiktok: tkRef };

  const trimmed = brandName.trim();
  const forcedSlug = trimmed
    ? trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || null
    : null;

  // Reflect actual state from compReports for the current slug
  const existingBrand = forcedSlug ? compReports[forcedSlug] : null;
  const loadedPlats = existingBrand
    ? COMP_PLATS.filter(p => existingBrand[p.key]).map(p => p.key)
    : [];
  const anyUploaded = loadedPlats.length > 0 || Object.keys(uploaded).length > 0;

  const handleUpload = (file, platKey) => {
    onUpload(file, platKey, forcedSlug, trimmed || null);
    setUploaded(prev => ({ ...prev, [platKey]: true }));
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 20px' }}>
      <div style={{ ...S.card, padding: '32px 40px', textAlign: 'center', maxWidth: 440 }}>
        <div style={{ fontSize: 34, marginBottom: 10 }}>🏆</div>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: '#1a1a1a' }}>Agregar nueva marca</div>
        <div style={{ fontSize: 13, color: '#888', marginBottom: 20, lineHeight: 1.5 }}>
          Escribe el nombre de la marca y sube los archivos Excel.<br />
          Puedes cargar 1, 2 o las 3 redes sociales.
        </div>

        {/* Brand name input */}
        <input
          type="text"
          placeholder="Nombre de la marca (ej: Preunic)"
          value={brandName}
          onChange={e => { setBrandName(e.target.value); setUploaded({}); }}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '10px 14px', marginBottom: 18,
            border: '1.5px solid #d1d5db', borderRadius: 8,
            fontSize: 14, outline: 'none',
            fontFamily: 'inherit', color: '#1a1a1a',
          }}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'stretch' }}>
          {COMP_PLATS.map(p => {
            const isDone = loadedPlats.includes(p.key) || uploaded[p.key];
            return (
              <div key={p.key}>
                <button
                  disabled={!trimmed}
                  style={{ ...S.btnSecondary, display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 20px', width: '100%', justifyContent: 'center',
                    cursor: trimmed ? 'pointer' : 'not-allowed',
                    opacity: trimmed ? 1 : 0.45,
                    ...(isDone ? { borderColor: '#059669', color: '#059669', background: '#f0fdf4' } : {}) }}
                  onClick={() => trimmed && refs[p.key].current.click()}
                >
                  <span style={{ ...S.platIcon, background: isDone ? '#059669' : p.iconBg,
                    fontSize: p.key === 'tiktok' ? 9 : 11 }}>{isDone ? '✓' : p.icon}</span>
                  {isDone ? `${p.label} cargado` : `↑ Cargar ${p.label}`}
                </button>
                <input ref={refs[p.key]} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                  onChange={e => { if (e.target.files[0]) { handleUpload(e.target.files[0], p.key); e.target.value = ''; } }} />
              </div>
            );
          })}
        </div>

        {/* Go to brand button after at least one upload */}
        {anyUploaded && forcedSlug && (
          <button
            style={{ ...S.btnPrimary, marginTop: 20, width: '100%', padding: '11px 20px',
              background: '#DC2626', border: 'none', color: '#fff', borderRadius: 8,
              fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => onGoToBrand(forcedSlug)}
          >
            Ver {trimmed} →
          </button>
        )}
      </div>
    </div>
  );
}

/* Platform sub-tabs + data for a single brand */
function BrandDashboard({ brand, brandSlug, isAdmin, onUpload, onRemovePlatform }) {
  const [compTab, setCompTab] = useState(() => {
    for (const p of COMP_PLATS) { if (brand[p.key]) return p.key; }
    return 'instagram';
  });
  const igRef = useRef();
  const fbRef = useRef();
  const tkRef = useRef();
  const refs = { instagram: igRef, facebook: fbRef, tiktok: tkRef };

  const r    = brand[compTab];
  const plat = COMP_PLATS.find(p => p.key === compTab);

  return (
    <>
      {/* Platform sub-tabs */}
      <div style={{ ...S.tabs, borderBottomColor: '#f0f0f0', marginBottom: 16 }}>
        {COMP_PLATS.map(p => (
          <button key={p.key} className="rp-tab-btn"
            style={{ ...S.tab, ...(compTab === p.key ? { ...S.tabActive, borderBottomColor: p.color, color: p.color } : {}) }}
            onClick={() => setCompTab(p.key)}
          >
            <span style={{ ...S.platIcon, background: p.iconBg, fontSize: p.key === 'tiktok' ? 9 : 11 }}>{p.icon}</span>
            {p.label}
            {brand[p.key] && isAdmin && (
              <button onClick={e => { e.stopPropagation(); onRemovePlatform(brandSlug, p.key); }} style={S.tabClose}>×</button>
            )}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {isAdmin && (
          <>
            <button style={{ ...S.btnSecondary, fontSize: 12, padding: '5px 12px' }}
              onClick={() => refs[compTab]?.current?.click()}>
              ↑ Cargar {plat?.label}
            </button>
            {COMP_PLATS.map(p => (
              <input key={p.key} ref={refs[p.key]} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) { onUpload(e.target.files[0], p.key, brandSlug); e.target.value = ''; } }} />
            ))}
          </>
        )}
      </div>

      {/* Content */}
      {!r ? (
        <div style={{ textAlign: 'center', padding: '50px 40px', color: '#999' }}>
          <div style={{ fontSize: 30, marginBottom: 10 }}>📁</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#555', marginBottom: 6 }}>
            Sin datos de {plat?.label} para {brand.brandName}
          </div>
          {isAdmin ? (
            <button style={{ ...S.btnSecondary, marginTop: 8 }}
              onClick={() => refs[compTab]?.current?.click()}>
              ↑ Cargar archivo Excel
            </button>
          ) : (
            <div style={{ fontSize: 13 }}>El administrador aún no ha cargado los datos.</div>
          )}
        </div>
      ) : (
        <CompetenciaPlatDashboard r={r} plat={plat} />
      )}
    </>
  );
}

/* Top-level Competencia dashboard — brand tabs */
function CompetenciaDashboard({ compReports, isAdmin, onUpload, onRemoveBrand, onRemovePlatform }) {
  const brands = Object.keys(compReports);
  const [activeBrand, setActiveBrand] = useState(() => brands[0] || '__new__');

  // Auto-select first brand only when there are no brands yet and one appears
  const prevBrandsLen = useRef(brands.length);
  useEffect(() => {
    // Only auto-switch if we had 0 brands before (first ever brand added from empty state)
    if (activeBrand === '__new__' && brands.length > 0 && prevBrandsLen.current === 0) {
      setActiveBrand(brands[0]);
    }
    prevBrandsLen.current = brands.length;
  }, [brands.length]); // eslint-disable-line

  const currentBrand = compReports[activeBrand];

  return (
    <div style={S.content} className="rp-content">
      {/* Brand-level tabs */}
      <div style={{ ...S.tabs, borderBottomColor: '#e8edf5', marginBottom: 0 }}>
        {brands.map(slug => {
          const b = compReports[slug];
          return (
            <button key={slug} className="rp-tab-btn"
              style={{ ...S.tab, ...(activeBrand === slug && activeBrand !== '__new__'
                ? { ...S.tabActive, borderBottomColor: '#DC2626', color: '#DC2626' } : {}) }}
              onClick={() => setActiveBrand(slug)}
            >
              <span style={{ ...S.platIcon, background: '#DC2626', fontSize: 9 }}>VS</span>
              {b.brandName || slug}
              {isAdmin && (
                <button onClick={e => { e.stopPropagation(); onRemoveBrand(slug); }} style={S.tabClose}>×</button>
              )}
            </button>
          );
        })}

        {/* Add brand */}
        {isAdmin && (
          <button className="rp-tab-btn"
            style={{ ...S.tab, ...(activeBrand === '__new__'
              ? { ...S.tabActive, borderBottomColor: '#6B7280', color: '#6B7280' } : {}) }}
            onClick={() => setActiveBrand('__new__')}
          >
            + Agregar marca
          </button>
        )}
        <div style={{ flex: 1 }} />
      </div>

      {/* Content */}
      {activeBrand === '__new__' ? (
        <NewBrandPanel onUpload={onUpload} compReports={compReports} onGoToBrand={setActiveBrand} />
      ) : currentBrand ? (
        <BrandDashboard
          brand={currentBrand}
          brandSlug={activeBrand}
          isAdmin={isAdmin}
          onUpload={onUpload}
          onRemovePlatform={onRemovePlatform}
        />
      ) : brands.length === 0 && !isAdmin ? (
        <div style={{ textAlign: 'center', padding: '80px 40px', color: '#999' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏆</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Sin datos de competencia</div>
          <div style={{ fontSize: 13 }}>El administrador aún no ha cargado los datos.</div>
        </div>
      ) : null}
    </div>
  );
}

/* ─── Comparación dashboard ──────────────────────────────────────────── */
const BRAND_PALETTE = ['#4A4A8A', '#DC2626', '#D97706', '#059669', '#7C3AED', '#0284C7', '#DB2777'];

function aggregateBrand(platformReports, name, color) {
  const fb = platformReports?.facebook;
  const ig = platformReports?.instagram;
  const tk = platformReports?.tiktok;
  function kv(r, ...names) {
    if (!r?.kpis) return 0;
    for (const n of names) {
      const k = r.kpis.find(k => k.kpi === n);
      if (k && k.curr !== null && k.curr !== undefined) return Number(k.curr) || 0;
    }
    return 0;
  }
  return {
    name, color,
    followers:  kv(ig, 'Total Followers') + kv(fb, 'Total Followers') + kv(tk, 'Follower Count'),
    engagement: kv(ig, 'Total Organic Engagement') + kv(fb, 'Total Engagement') + kv(tk, 'Total Engagement'),
    posts:      kv(ig, 'Total Organic Posts') + kv(fb, 'Total Posts') + kv(tk, 'Total Posts'),
    reach:      kv(fb, 'Page Reach'),
    views:      kv(ig, 'Total Posts Views') + kv(tk, 'Total Views'),
    emv:        kv(ig, 'Earned Media Value') + kv(fb, 'Earned Media Value') + kv(tk, 'Earned Media Value'),
    ig: { followers: kv(ig, 'Total Followers'),  engagement: kv(ig, 'Total Organic Engagement'), posts: kv(ig, 'Total Organic Posts'), avgEng: kv(ig, 'Avg. Engagement') },
    fb: { followers: kv(fb, 'Total Followers'),  engagement: kv(fb, 'Total Engagement'),          posts: kv(fb, 'Total Posts'),          avgEng: kv(fb, 'Avg Engagement') },
    tk: { followers: kv(tk, 'Follower Count'),   engagement: kv(tk, 'Total Engagement'),          posts: kv(tk, 'Total Posts'),          avgEng: kv(tk, 'Avg. Engagement') },
  };
}

function ComparacionDashboard({ reports, compReports }) {
  const compBrands = Object.entries(compReports);

  if (compBrands.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 40px', color: '#999' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Sin datos de competencia</div>
        <div style={{ fontSize: 13 }}>Agrega datos en la pestaña Competencia para comparar.</div>
      </div>
    );
  }

  // Detect Maicao name from reports (strip platform suffix e.g. "Maicao Instagram Report" → "Maicao")
  const maicaoName = cleanBrandName(reports?.instagram?.profile || reports?.facebook?.profile || reports?.tiktok?.profile || 'Maicao');

  const brands = [
    aggregateBrand(reports, maicaoName, BRAND_PALETTE[0]),
    ...compBrands.map(([slug, b], i) =>
      aggregateBrand(
        { instagram: b.instagram, facebook: b.facebook, tiktok: b.tiktok },
        b.brandName || slug,
        BRAND_PALETTE[(i + 1) % BRAND_PALETTE.length],
      )
    ),
  ];

  const RANKINGS = [
    { key: 'followers',  label: 'Seguidores Totales'  },
    { key: 'engagement', label: 'Interacciones Totales' },
    { key: 'posts',      label: 'Posts Totales'        },
    { key: 'reach',      label: 'Alcance (Facebook)'   },
    { key: 'views',      label: 'Vistas (IG + TK)'     },
    { key: 'emv',        label: 'Valor Medios Ganados'  },
  ].filter(m => brands.some(b => b[m.key] > 0));

  const PLAT_SECTIONS = [
    {
      label: 'Seguidores por red social',
      rows: [
        { plat: 'Instagram', values: brands.map(b => ({ name: b.name, value: b.ig.followers, fill: b.color })) },
        { plat: 'Facebook',  values: brands.map(b => ({ name: b.name, value: b.fb.followers, fill: b.color })) },
        { plat: 'TikTok',    values: brands.map(b => ({ name: b.name, value: b.tk.followers, fill: b.color })) },
      ],
    },
    {
      label: 'Interacciones por red social',
      rows: [
        { plat: 'Instagram', values: brands.map(b => ({ name: b.name, value: b.ig.engagement, fill: b.color })) },
        { plat: 'Facebook',  values: brands.map(b => ({ name: b.name, value: b.fb.engagement, fill: b.color })) },
        { plat: 'TikTok',    values: brands.map(b => ({ name: b.name, value: b.tk.engagement, fill: b.color })) },
      ],
    },
    {
      label: 'Posts por red social',
      rows: [
        { plat: 'Instagram', values: brands.map(b => ({ name: b.name, value: b.ig.posts, fill: b.color })) },
        { plat: 'Facebook',  values: brands.map(b => ({ name: b.name, value: b.fb.posts, fill: b.color })) },
        { plat: 'TikTok',    values: brands.map(b => ({ name: b.name, value: b.tk.posts, fill: b.color })) },
      ],
    },
  ];

  const medals = ['🥇', '🥈', '🥉'];

  const TABLE_ROWS = [
    { label: 'TOTALES', isHeader: true },
    { label: 'Seguidores totales',    fn: b => b.followers  },
    { label: 'Interacciones totales', fn: b => b.engagement },
    { label: 'Posts totales',         fn: b => b.posts      },
    { label: 'Alcance (Facebook)',    fn: b => b.reach, skip: bs => bs.every(b => !b.reach) },
    { label: 'Vistas (IG + TK)',      fn: b => b.views, skip: bs => bs.every(b => !b.views) },
    { label: 'EMV total',             fn: b => b.emv,   skip: bs => bs.every(b => !b.emv) },
    { label: 'INSTAGRAM', isHeader: true, skip: bs => bs.every(b => !b.ig.followers && !b.ig.engagement) },
    { label: 'IG Seguidores',         fn: b => b.ig.followers,  skip: bs => bs.every(b => !b.ig.followers) },
    { label: 'IG Interacciones',      fn: b => b.ig.engagement, skip: bs => bs.every(b => !b.ig.engagement) },
    { label: 'IG Posts',              fn: b => b.ig.posts,      skip: bs => bs.every(b => !b.ig.posts) },
    { label: 'IG Avg. Interacciones', fn: b => b.ig.avgEng,     skip: bs => bs.every(b => !b.ig.avgEng) },
    { label: 'FACEBOOK', isHeader: true, skip: bs => bs.every(b => !b.fb.followers && !b.fb.engagement) },
    { label: 'FB Seguidores',         fn: b => b.fb.followers,  skip: bs => bs.every(b => !b.fb.followers) },
    { label: 'FB Interacciones',      fn: b => b.fb.engagement, skip: bs => bs.every(b => !b.fb.engagement) },
    { label: 'FB Posts',              fn: b => b.fb.posts,      skip: bs => bs.every(b => !b.fb.posts) },
    { label: 'FB Avg. Interacciones', fn: b => b.fb.avgEng,     skip: bs => bs.every(b => !b.fb.avgEng) },
    { label: 'TIKTOK', isHeader: true, skip: bs => bs.every(b => !b.tk.followers && !b.tk.engagement) },
    { label: 'TK Seguidores',         fn: b => b.tk.followers,  skip: bs => bs.every(b => !b.tk.followers) },
    { label: 'TK Interacciones',      fn: b => b.tk.engagement, skip: bs => bs.every(b => !b.tk.engagement) },
    { label: 'TK Posts',              fn: b => b.tk.posts,      skip: bs => bs.every(b => !b.tk.posts) },
    { label: 'TK Avg. Interacciones', fn: b => b.tk.avgEng,     skip: bs => bs.every(b => !b.tk.avgEng) },
  ];

  return (
    <div style={S.content} className="rp-content">

      {/* ── Leyenda de marcas ── */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center',
        padding: '12px 18px', background: '#fff', borderRadius: 10,
        border: '1px solid #e8edf5', boxShadow: '0 1px 4px rgba(0,0,0,.04)' }}>
        <span style={{ fontSize: 11, color: '#aaa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px' }}>Marcas:</span>
        {brands.map((b, i) => (
          <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
            fontWeight: i === 0 ? 700 : 500, color: b.color }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: b.color, display: 'inline-block' }} />
            {b.name}
            {i === 0 && (
              <span style={{ fontSize: 9, fontWeight: 800, color: b.color,
                background: b.color + '18', borderRadius: 20, padding: '1px 7px', letterSpacing: '.5px', textTransform: 'uppercase' }}>
                MAICAO
              </span>
            )}
          </span>
        ))}
      </div>

      {/* ── Ranking cards ── */}
      <div data-pdf-block="true" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 14 }}>
        {RANKINGS.map(m => {
          const sorted = [...brands].filter(b => b[m.key] > 0).sort((a, b2) => b2[m.key] - a[m.key]);
          if (!sorted.length) return null;
          const maxVal = sorted[0][m.key];
          return (
            <div key={m.key} style={{ ...S.card, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase',
                letterSpacing: '.5px', marginBottom: 14 }}>{m.label}</div>
              {sorted.map((b, i) => (
                <div key={b.name} style={{ marginBottom: i < sorted.length - 1 ? 12 : 0,
                  padding: i === 0 ? '6px 8px' : '2px 0',
                  background: i === 0 ? b.color + '0d' : 'transparent', borderRadius: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                    <span style={{ fontSize: 12, fontWeight: i === 0 ? 700 : 500, color: b.color }}>
                      {medals[i] || `${i + 1}.`} {b.name}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: i === 0 ? b.color : '#444' }}>
                      {fmt(b[m.key])}
                    </span>
                  </div>
                  <div style={{ height: 5, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 4, background: b.color,
                      width: `${Math.max(2, Math.round((b[m.key] / maxVal) * 100))}%` }} />
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* ── Gráficos comparativos por plataforma ── */}
      {PLAT_SECTIONS.map((section, si) => {
        const chartData = section.rows.map(row => {
          const entry = { plat: row.plat };
          row.values.forEach(v => { entry[v.name] = v.value; });
          return entry;
        });
        if (chartData.every(d => brands.every(b => !d[b.name]))) return null;
        return (
          <Card key={si}>
            <SectionTitle>{section.label}</SectionTitle>
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="plat" tick={{ fontSize: 12, fontWeight: 600 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={fmt} />
                <Tooltip formatter={(v, name) => [fmt(v), name]} />
                <Legend />
                {brands.map((b, i) => (
                  <Bar key={b.name} dataKey={b.name} fill={b.color} radius={[4, 4, 0, 0]} opacity={i === 0 ? 1 : 0.82} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Card>
        );
      })}

      {/* ── Tabla comparativa completa ── */}
      <Card>
        <SectionTitle>Tabla comparativa completa</SectionTitle>
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ ...S.th, minWidth: 160 }}>Métrica</th>
                {brands.map((b, i) => (
                  <th key={i} style={{ ...S.th, color: b.color, background: b.color + '14', textAlign: 'center' }}>
                    {i === 0 ? '★ ' : ''}{b.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TABLE_ROWS.map((row, ri) => {
                if (row.skip && row.skip(brands)) return null;
                if (row.isHeader) {
                  return (
                    <tr key={ri}>
                      <td colSpan={brands.length + 1} style={{ ...S.td, fontWeight: 700, color: '#555',
                        background: '#f4f6fb', fontSize: 10, textTransform: 'uppercase',
                        letterSpacing: '.6px', padding: '8px 10px', borderBottom: '2px solid #e8edf5' }}>
                        {row.label}
                      </td>
                    </tr>
                  );
                }
                const values = brands.map(b => row.fn(b));
                const maxVal = Math.max(...values.filter(v => v > 0));
                return (
                  <tr key={ri} style={ri % 2 === 0 ? S.trEven : {}}>
                    <td style={{ ...S.td, color: '#555' }}>{row.label}</td>
                    {brands.map((b, i) => {
                      const v = values[i];
                      const isMax = v > 0 && v === maxVal && brands.length > 1;
                      const isMaicao = i === 0;
                      return (
                        <td key={i} style={{ ...S.tdNum,
                          fontWeight: isMax || isMaicao ? 700 : 400,
                          color: isMax ? b.color : isMaicao ? BRAND_PALETTE[0] : '#333',
                          background: isMax ? b.color + '0d' : 'transparent',
                        }}>
                          {v > 0 ? fmt(v) : '—'}
                          {isMax && ' ▲'}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

    </div>
  );
}

/* ─── main page ──────────────────────────────────────────────────────── */
export default function ReportePage({ user }) {
  const isAdmin = user && (user.username === 'admin' || user.is_staff || user.is_superuser);
  const [reports, setReports]         = useState({});
  const [compReports, setCompReports] = useState({});
  const [activeTab, setActive]        = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [dragging, setDragging] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loadingRemote, setLoadingRemote] = useState(true);
  const [copied, setCopied]     = useState(false);
  const inputRef = useRef();

  const handleShare = useCallback(() => {
    const url = 'https://calendario.geves.cl/reporte';
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }).catch(() => {
      // fallback para navegadores sin clipboard API
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }, []);

  // Inyectar CSS responsive
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'rp-responsive';
    style.textContent = RESPONSIVE_CSS;
    document.head.appendChild(style);
    return () => { const el = document.getElementById('rp-responsive'); if (el) el.remove(); };
  }, []);

  // Cargar reportes persistidos al montar
  useEffect(() => {
    contentService.getReportesSociales()
      .then(res => {
        if (res.data && res.data.length > 0) {
          const loaded = {};
          const compLoaded = {};
          res.data.forEach(r => {
            if (r.plataforma.startsWith('comp_')) {
              const inner = r.plataforma.slice(5); // strip 'comp_'
              let platform = null;
              let brandSlug = 'marca';
              for (const p of ['instagram', 'facebook', 'tiktok']) {
                if (inner === p) {
                  // Old format: comp_instagram
                  platform  = p;
                  brandSlug = slugify(cleanBrandName(r.perfil || r.datos?.profile || 'marca'));
                  break;
                } else if (inner.endsWith('_' + p) && inner.length > p.length + 1) {
                  // New format: comp_preunic_instagram
                  platform  = p;
                  brandSlug = inner.slice(0, -(p.length + 1));
                  break;
                }
              }
              if (platform) {
                const rawBrandName = r.datos?._brandName || r.perfil || r.datos?.profile || brandSlug;
                const brandName    = cleanBrandName(rawBrandName);
                // Re-slug with cleaned name so old dirty slugs normalize
                const cleanSlug = slugify(brandName);
                if (!compLoaded[cleanSlug]) compLoaded[cleanSlug] = { brandName, _dbKeys: {} };
                if (!compLoaded[cleanSlug]._dbKeys) compLoaded[cleanSlug]._dbKeys = {};
                compLoaded[cleanSlug]._dbKeys[platform] = r.plataforma; // exact DB key
                compLoaded[cleanSlug][platform] = r.datos;
              }
            } else {
              loaded[r.plataforma] = r.datos;
            }
          });
          if (Object.keys(loaded).length > 0) {
            setReports(loaded);
            setActive('resumen');
          }
          if (Object.keys(compLoaded).length > 0) {
            setCompReports(compLoaded);
            if (Object.keys(loaded).length === 0) setActive('competencia');
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoadingRemote(false));
  }, []);

  const processFile = useCallback((file) => {
    if (!file) return;
    setLoading(true); setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb     = XLSX.read(e.target.result, { type: 'array' });
        const parsed = parseReport(wb);
        setReports(prev => ({ ...prev, [parsed.platform]: parsed }));
        setActive(parsed.platform);
        // Persistir en backend
        contentService.saveReporteSocial({
          plataforma:  parsed.platform,
          perfil:      parsed.profile  || '',
          fecha_rango: parsed.dateRange || '',
          datos:       parsed,
        }).catch(err => console.error('Error guardando reporte:', err));
      } catch (err) {
        setError('No se pudo leer el archivo. Asegúrate de que sea un archivo Excel válido.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const processCompFile = useCallback((file, platform, forceBrandSlug = null, forceBrandName = null) => {
    if (!file) return;
    setLoading(true); setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb            = XLSX.read(e.target.result, { type: 'array' });
        const parsed        = parseCompReport(wb, platform);
        const autoBrandName = cleanBrandName(parsed.profile || 'Competencia');
        const brandSlug     = forceBrandSlug || slugify(forceBrandName || autoBrandName);
        const dbKey = `comp_${brandSlug}_${platform}`;
        setCompReports(prev => {
          const existingName  = prev[brandSlug]?.brandName;
          const resolvedName  = forceBrandName || existingName || autoBrandName;
          return {
            ...prev,
            [brandSlug]: {
              ...(prev[brandSlug] || {}),
              brandName: resolvedName,
              _dbKeys: { ...(prev[brandSlug]?._dbKeys || {}), [platform]: dbKey },
              [platform]: parsed,
            },
          };
        });
        setActive('competencia');
        const displayName = forceBrandName || autoBrandName;
        contentService.saveReporteSocial({
          plataforma:  `comp_${brandSlug}_${platform}`,
          perfil:      displayName,
          fecha_rango: parsed.dateRange || '',
          datos:       { ...parsed, _brandSlug: brandSlug, _brandName: displayName },
        }).catch(err => console.error('Error guardando reporte competencia:', err));
      } catch (err) {
        setError('No se pudo leer el archivo de competencia. Verifica que sea un archivo Excel válido.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const handleFile   = e => processFile(e.target.files[0]);
  const handleDrop   = e => { e.preventDefault(); setDragging(false); processFile(e.dataTransfer.files[0]); };
  const handleRemove = plat => {
    setReports(prev => { const n = { ...prev }; delete n[plat]; return n; });
    if (activeTab === plat) {
      const remaining = Object.keys(reports).filter(k => k !== plat);
      setActive(remaining.length > 0 ? 'resumen' : null);
    }
    // Eliminar del backend (solo admin)
    if (isAdmin) {
      contentService.deleteReporteSocial(plat).catch(() => {});
    }
  };

  const handleRemoveBrand = (brandSlug) => {
    // Collect all DB keys for this brand BEFORE clearing state
    const brand = compReports[brandSlug];
    const dbKeys = brand?._dbKeys || {};
    setCompReports(prev => {
      const n = { ...prev };
      delete n[brandSlug];
      if (Object.keys(n).length === 0 && activeTab === 'competencia') {
        setActive(Object.keys(reports).length > 0 ? 'resumen' : null);
      }
      return n;
    });
    if (isAdmin) {
      // Delete using exact DB keys stored when data was loaded/uploaded
      const keysToDelete = Object.values(dbKeys);
      if (keysToDelete.length > 0) {
        keysToDelete.forEach(key => contentService.deleteReporteSocial(key).catch(() => {}));
      } else {
        // Fallback: try reconstructed keys
        ['instagram', 'facebook', 'tiktok'].forEach(plat => {
          contentService.deleteReporteSocial(`comp_${brandSlug}_${plat}`).catch(() => {});
        });
      }
      // Also try old-format flat keys for backward compat
      ['instagram', 'facebook', 'tiktok'].forEach(plat => {
        contentService.deleteReporteSocial(`comp_${plat}`).catch(() => {});
      });
    }
  };

  const handleRemoveCompPlatform = (brandSlug, platform) => {
    const brand = compReports[brandSlug];
    const dbKey = brand?._dbKeys?.[platform] || `comp_${brandSlug}_${platform}`;
    setCompReports(prev => {
      const n = { ...prev };
      if (n[brandSlug]) {
        n[brandSlug] = { ...n[brandSlug] };
        delete n[brandSlug][platform];
        const hasData = ['instagram', 'facebook', 'tiktok'].some(p => n[brandSlug][p]);
        if (!hasData) delete n[brandSlug];
      }
      return n;
    });
    if (isAdmin) {
      contentService.deleteReporteSocial(dbKey).catch(() => {});
    }
  };

  /* ── PDF export ── */
  const exportToPDF = useCallback(async () => {
    if (exporting) return;
    setExporting(true);

    const savedTab = activeTab;
    const hasPlatforms = ['facebook', 'instagram', 'tiktok'].some(p => reports[p]);
    const platforms = [
      ...(hasPlatforms ? ['resumen'] : []),
      ...['facebook', 'instagram', 'tiktok'].filter(p => reports[p]),
    ];

    const platMeta = {
      resumen:   { label: 'Resumen General', rgb: [74, 74, 138]  },
      facebook:  { label: 'Facebook',        rgb: [24, 119, 242] },
      instagram: { label: 'Instagram',       rgb: [131, 58, 180] },
      tiktok:    { label: 'TikTok',          rgb: [238, 29, 82]  },
    };

    const pdf = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
    const pageW  = pdf.internal.pageSize.getWidth();   // 595
    const pageH  = pdf.internal.pageSize.getHeight();  // 842
    const margin = 28;
    const usableW = pageW - margin * 2;
    let y = margin;
    let firstPlat = true;

    for (const plat of platforms) {
      // Switch tab and wait for recharts to repaint
      await new Promise(resolve => {
        flushSync(() => setActive(plat));
        setTimeout(resolve, 700);
      });

      if (!firstPlat) { pdf.addPage(); y = margin; }
      firstPlat = false;

      // Platform header bar
      const meta = platMeta[plat];
      const rp   = plat !== 'resumen' ? reports[plat] : null;
      pdf.setFillColor(...meta.rgb);
      pdf.rect(margin, y, usableW, 38, 'F');
      pdf.setFontSize(16);
      pdf.setTextColor(255, 255, 255);
      pdf.text(meta.label, margin + 12, y + 25);
      if (rp && rp.dateRange) {
        pdf.setFontSize(9);
        pdf.text(rp.dateRange, pageW - margin - 6, y + 25, { align: 'right' });
      }
      y += 48;

      // Capture each card block individually (avoids cuts)
      const blocks = Array.from(document.querySelectorAll('[data-pdf-block]'));

      for (const block of blocks) {
        let canvas;
        try {
          canvas = await html2canvas(block, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            removeContainer: true,
          });
        } catch (_) { continue; }

        let drawW = usableW;
        let drawH = (canvas.height / canvas.width) * drawW;

        // Scale down if taller than one full page
        if (drawH > pageH - margin * 2) {
          const s = (pageH - margin * 2) / drawH;
          drawW *= s; drawH *= s;
        }

        // New page if block doesn't fit in remaining space
        if (y + drawH > pageH - margin) {
          pdf.addPage();
          y = margin;
        }

        pdf.addImage(canvas.toDataURL('image/jpeg', 0.88), 'JPEG', margin, y, drawW, drawH);
        y += drawH + 10;
      }
    }

    // Restore original tab
    flushSync(() => setActive(savedTab));
    const date = new Date().toISOString().slice(0, 10);
    pdf.save(`Reporte_Redes_Sociales_${date}.pdf`);
    setExporting(false);
  }, [reports, activeTab, exporting]);

  const hasFB   = !!reports.facebook;
  const hasIG   = !!reports.instagram;
  const hasTK   = !!reports.tiktok;
  const hasAny  = hasFB || hasIG || hasTK;
  const hasComp = Object.keys(compReports).length > 0;
  const current = activeTab && reports[activeTab];

  const fbAccent = FB_BLUE;
  const igAccent = IG_GRAD1;

  return (
    <div style={S.page} className="rp-page">
      {/* ── Header ── */}
      <div style={S.header} className="rp-header">
        <div>
          <h1 style={S.pageTitle} className="rp-page-title">Reporte Redes Sociales</h1>
          {activeTab === 'resumen' && hasAny && (
            <p style={S.subtitle} className="rp-subtitle">
              {[reports.facebook, reports.instagram, reports.tiktok].filter(Boolean).map(r => r.profile).filter(Boolean).join(' · ')}
            </p>
          )}
          {current && activeTab !== 'resumen' && <p style={S.subtitle} className="rp-subtitle">{current.profile} · {current.dateRange}</p>}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }} className="rp-header-actions">
          {hasAny && (
            <div style={{ position: 'relative' }}>
              <button style={S.btnShare} onClick={handleShare}>
                🔗 Compartir
              </button>
              {copied && (
                <div style={S.toast}>¡Enlace copiado!</div>
              )}
            </div>
          )}
          {isAdmin && (
            <button style={S.btnPrimary} onClick={() => inputRef.current.click()}>
              ↑ Cargar Excel
            </button>
          )}
        </div>
        <input ref={inputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFile} />
      </div>

      {/* ── Platform Tabs ── */}
      {(hasAny || hasComp || isAdmin) && (
        <div style={S.tabs} className="rp-tabs">
          {/* Resumen siempre como primera pestaña (solo si hay datos) */}
          {hasAny && (
            <button
              className="rp-tab-btn"
              style={{ ...S.tab, ...(activeTab === 'resumen' ? { ...S.tabActive, borderBottomColor: '#4A4A8A', color: '#4A4A8A' } : {}) }}
              onClick={() => setActive('resumen')}
            >
              <span style={{ ...S.platIcon, background: '#4A4A8A', fontSize: 9 }}>∑</span>
              Resumen
            </button>
          )}
          {hasFB && (
            <button
              className="rp-tab-btn"
              style={{ ...S.tab, ...(activeTab === 'facebook' ? { ...S.tabActive, borderBottomColor: fbAccent, color: fbAccent } : {}) }}
              onClick={() => setActive('facebook')}
            >
              <span style={{ ...S.platIcon, background: fbAccent }}>f</span>
              Facebook
              <button onClick={e => { e.stopPropagation(); handleRemove('facebook'); }} style={S.tabClose}>×</button>
            </button>
          )}
          {hasIG && (
            <button
              className="rp-tab-btn"
              style={{ ...S.tab, ...(activeTab === 'instagram' ? { ...S.tabActive, borderBottomColor: igAccent, color: igAccent } : {}) }}
              onClick={() => setActive('instagram')}
            >
              <span style={{ ...S.platIcon, background: 'linear-gradient(45deg, #833AB4, #E1306C, #F77737)' }}>IG</span>
              Instagram
              <button onClick={e => { e.stopPropagation(); handleRemove('instagram'); }} style={S.tabClose}>×</button>
            </button>
          )}
          {hasTK && (
            <button
              className="rp-tab-btn"
              style={{ ...S.tab, ...(activeTab === 'tiktok' ? { ...S.tabActive, borderBottomColor: TK_RED, color: TK_RED } : {}) }}
              onClick={() => setActive('tiktok')}
            >
              <span style={{ ...S.platIcon, background: '#010101', fontSize: 9, letterSpacing: '-0.5px' }}>TK</span>
              TikTok
              <button onClick={e => { e.stopPropagation(); handleRemove('tiktok'); }} style={S.tabClose}>×</button>
            </button>
          )}
          {/* ── Competencia tab ── */}
          <button
            className="rp-tab-btn"
            style={{ ...S.tab, ...(activeTab === 'competencia' ? { ...S.tabActive, borderBottomColor: '#DC2626', color: '#DC2626' } : {}) }}
            onClick={() => setActive('competencia')}
          >
            <span style={{ ...S.platIcon, background: '#DC2626', fontSize: 9, letterSpacing: '-0.5px' }}>VS</span>
            Competencia
          </button>
          {/* ── Comparación tab (visible cuando hay datos propios y de competencia) ── */}
          {(hasAny || hasComp) && (
            <button
              className="rp-tab-btn"
              style={{ ...S.tab, ...(activeTab === 'comparacion' ? { ...S.tabActive, borderBottomColor: '#059669', color: '#059669' } : {}) }}
              onClick={() => setActive('comparacion')}
            >
              <span style={{ ...S.platIcon, background: '#059669', fontSize: 9, letterSpacing: '-0.5px' }}>⇄</span>
              Comparación
            </button>
          )}
          <div style={{ flex: 1 }} />
          {isAdmin && (
            <button className="rp-tab-add" style={S.btnSecondary} onClick={() => inputRef.current.click()}>
              + Agregar otra red
            </button>
          )}
        </div>
      )}

      {/* ── Upload zone (drag & drop invisible, solo activa) ── */}
      {!hasAny && !loading && isAdmin && activeTab !== 'competencia' && (
        <div
          style={{ ...S.dropzone, ...(dragging ? S.dropzoneDrag : {}), border: 'none', background: 'transparent', padding: '40px 0' }}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          {error && <div style={{ textAlign: 'center', color: '#c62828', fontSize: 14 }}>{error}</div>}
        </div>
      )}

      {loadingRemote && (
        <Loading fullScreen={false} mensaje="Cargando reportes" />
      )}

      {!loadingRemote && !hasAny && !hasComp && !loading && !isAdmin && (
        <div style={{ textAlign: 'center', padding: '80px 40px', color: '#999' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>No hay reportes disponibles</div>
          <div style={{ fontSize: 14 }}>El administrador aún no ha cargado los datos del período.</div>
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: '80px 0', color: '#666' }}>Procesando archivo…</div>}

      {/* ── Dashboard ── */}
      {hasAny  && activeTab === 'resumen'      && <ResumenDashboard   reports={reports} />}
      {current && activeTab === 'facebook'     && <FacebookDashboard  r={current} />}
      {current && activeTab === 'instagram'    && <InstagramDashboard r={current} />}
      {current && activeTab === 'tiktok'       && <TikTokDashboard    r={current} />}
      {activeTab === 'competencia'             && (
        <CompetenciaDashboard
          compReports={compReports}
          isAdmin={isAdmin}
          onUpload={processCompFile}
          onRemoveBrand={handleRemoveBrand}
          onRemovePlatform={handleRemoveCompPlatform}
        />
      )}
      {activeTab === 'comparacion'             && (
        <ComparacionDashboard reports={reports} compReports={compReports} />
      )}

      {/* Small hint when platform tab is active */}
      {hasAny && !loading && activeTab && activeTab !== 'resumen' && activeTab !== 'competencia' && activeTab !== 'comparacion' && reports[activeTab] && (
        <div style={{ textAlign: 'center', marginTop: 8, fontSize: 12, color: '#bbb' }}>
          {reports[activeTab]?.profile} · {reports[activeTab]?.dateRange}
        </div>
      )}
    </div>
  );
}

/* ─── styles ─────────────────────────────────────────────────────────── */
const S = {
  page:        { padding: '28px 32px', maxWidth: 1200, margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', color: '#1a1a1a' },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 12, flexWrap: 'wrap' },
  pageTitle:   { margin: 0, fontSize: 22, fontWeight: 700 },
  subtitle:    { margin: '4px 0 0', color: '#666', fontSize: 13 },
  btnPrimary:  { padding: '8px 18px', background: '#1877F2', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  btnShare:    { padding: '8px 18px', background: '#111', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  toast:       { position: 'absolute', top: '110%', right: 0, background: '#222', color: '#fff', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 8, whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,.18)', zIndex: 100 },
  btnSecondary:{ padding: '7px 14px', background: '#fff', color: '#444', border: '1px solid #ddd', borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: 'pointer' },
  tabs:        { display: 'flex', alignItems: 'center', gap: 4, borderBottom: '2px solid #eee', marginBottom: 20 },
  tab:         { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', background: 'none', border: 'none', borderBottom: '3px solid transparent', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#666', marginBottom: -2, transition: 'color .15s' },
  tabActive:   { fontWeight: 700 },
  tabClose:    { marginLeft: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', fontSize: 16, lineHeight: 1, padding: 0 },
  platIcon:    { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', color: '#fff', fontWeight: 800, fontSize: 11, lineHeight: 1, flexShrink: 0 },
  dropzone:    { border: '2px dashed #b0c4de', borderRadius: 16, padding: '60px 40px', textAlign: 'center', cursor: 'pointer', background: '#f8fbff', transition: 'all .2s' },
  dropzoneDrag:{ borderColor: '#1877F2', background: '#e8f0fe' },
  content:     { display: 'flex', flexDirection: 'column', gap: 20 },
  row:         { display: 'flex', gap: 16, flexWrap: 'wrap' },
  card:        { background: '#fff', border: '1px solid #e8edf5', borderRadius: 12, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,.06)', minWidth: 0 },
  kpiGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: 12 },
  kpiCard:     { background: '#fff', border: '1px solid #e8edf5', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
  kpiLabel:    { fontSize: 11, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.4px', lineHeight: 1.3 },
  kpiValue:    { fontSize: 20, fontWeight: 700, marginBottom: 4 },
  kpiChange:   { fontSize: 11, fontWeight: 600 },
  kpiPrev:     { color: '#aaa', fontWeight: 400 },
  sectionTitle:{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#333' },
  postList:    { display: 'flex', flexDirection: 'column', gap: 10 },
  postCard:    { display: 'flex', gap: 12, padding: '12px 14px', background: '#f9fbff', borderRadius: 10, border: '1px solid #e4ecf7' },
  postRank:    { fontSize: 18, fontWeight: 800, minWidth: 28 },
  postMeta:    { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' },
  postType:    { color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '.4px' },
  postDate:    { fontSize: 12, color: '#777' },
  postLink:    { fontSize: 12, textDecoration: 'none', fontWeight: 500 },
  postMessage: { fontSize: 12, color: '#444', lineHeight: 1.5, marginBottom: 8, cursor: 'pointer', whiteSpace: 'pre-wrap' },
  postStats:   { display: 'flex', gap: 16, flexWrap: 'wrap' },
  stat:        { textAlign: 'center' },
  statValue:   { fontSize: 15, color: '#1a1a1a' },
  statLabel:   { fontSize: 10, color: '#999', marginTop: 1 },
  tag:         { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, display: 'inline-block', textTransform: 'capitalize' },
  tableWrap:   { overflowX: 'auto' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th:          { padding: '7px 10px', textAlign: 'left', background: '#f4f6fb', fontWeight: 600, color: '#555', fontSize: 11, whiteSpace: 'nowrap', position: 'sticky', top: 0 },
  td:          { padding: '6px 10px', color: '#333', borderBottom: '1px solid #f2f2f2', whiteSpace: 'nowrap' },
  tdNum:       { padding: '6px 10px', textAlign: 'right', color: '#333', borderBottom: '1px solid #f2f2f2', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  trEven:      { background: '#fafbff' },
};
